import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { parseDate } from '@progress/kendo-angular-intl';
import {
  SelectEvent,
  TabPosition,
  TabStripComponent,
} from '@progress/kendo-angular-layout';
import { SortableComponent } from '@progress/kendo-angular-sortable';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { DataWithPagingMetadata } from '~/infrastructure/paging';
import { PersonSummary } from '~/models/administration/person';
import { GridFieldAuthorization } from '~/models/administration/securityLevel';
import { UserSummary } from '~/models/administration/user';
import { EntityStatusOptions } from '~/models/entityStatus';
import { IAcquisitionTypeDto } from '~/models/modules/Asset/acquisitionType';
import {
  AssigneesType,
  CurrentSelectedAssetLocation,
  CurrentSelectedSupplier,
  IAssetAssignees,
  IAssetDto,
  IAssetModelRelatedFields,
} from '~/models/modules/Asset/asset';
import { assetFile } from '~/models/modules/Asset/assetFile';
import { IAssetModelDto } from '~/models/modules/Asset/assetModel';
import { IDepreciationMethodDto } from '~/models/modules/Asset/depreciationMethod';
import { FundingSourceDto } from '~/models/modules/Asset/fundingSource';
import { StatusDto } from '~/models/modules/Asset/status';
import { ConditionDto } from '~/models/modules/conditions';
import { CustomFieldSummary } from '~/models/modules/customField';
import { FormTabSummary } from '~/models/modules/formTab';
import { GridFieldSummary } from '~/models/modules/gridField';
import { GridFieldTextSummary } from '~/models/modules/GridFieldText';
import { LocationSummary } from '~/models/modules/location';
import { VendorSummary } from '~/models/modules/vendor';
import { UsersService } from '~/services/administration.ts/users';
import { EnvironmentLoaderService } from '~/services/BackEndURL/environment-loader.service';
import { GDataService } from '~/services/GDataService';
import { AcquisitionTypeService } from '~/services/modules/assets/acquisitionType.service';
import { AssetModelService } from '~/services/modules/assets/asset-model.service';
import { AssetService } from '~/services/modules/assets/asset.service';
import { ConditionsService } from '~/services/modules/assets/condition';
import { DepreciationMethodService } from '~/services/modules/assets/depreciationMethod.service';
import { FundingSourceService } from '~/services/modules/assets/fundingSource';
import { FundingSourceSettingsService } from '~/services/administration.ts/fundingSourceSettings';
import { ModelClassService } from '~/services/modules/assets/modelClass.service';
import { StatusesService } from '~/services/modules/assets/status';
import { BarcodeGeneratorsService } from '~/services/modules/stocks/barcode-generators';
import { CategoriesService } from '~/services/modules/stocks/categories';
import { CustomFieldsService } from '~/services/modules/stocks/custom-fields';
import { FilesService } from '~/services/modules/stocks/files';
import { GridFieldsService } from '~/services/modules/stocks/gridField';
import { LocationsService } from '~/services/modules/stocks/locations';
import { VendorsService } from '~/services/modules/stocks/vendors';
import { SecurityService } from '~/services/security.service';
import { SharedService } from '~/services/sharedService';
import { ThemeManagerService } from '~/services/theme-manager';
import { GridLayoutManagerService } from '~/shared/components/grid/layout/grid-layout-manager';
import { LayoutManagerService } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { NoteComponent } from '~/shared/components/note/note.component';
import { fileSources } from '~/shared/enums/fileSources';
import { PageState } from '~/shared/enums/stock-files.enum';
import { AssetModelSettingsService } from '~/services/modules/assets/assetModelSettings';
import { IAssetModelSettingsDto } from '~/models/modules/Asset/assetModelSettings';
import { FormatTypes } from '~/shared/enums/FormatTypes';
import { AssetAllHistoryService } from '~/services/modules/assets/history/assetAllHistory';
import { AccountsService } from '~/services/administration.ts/accounts';
import { SiteLisitng } from '~/models/administration/site';
import { MultiSiteService } from '~/services/modules/common/multi-site.service';
import moment from 'moment';
import { SitesEnum } from '~/shared/enums/sites.enum';
import { MultiSiteAwareComponent } from '~/infrastructure/multi-site-aware.component';
import { ScheduleMaintenanceService } from '~/services/modules/assets/transactions/schedule-maintenance.service';
import { PerformMaintenanceService } from '~/services/modules/assets/transactions/perform-maintenance.service';
import { PageNames } from '~/models/pageNames';

@Component({
    selector: 'app-upsert',
    templateUrl: './upsert.component.html',
    styleUrls: ['./upsert.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AssetUpsertComponent extends MultiSiteAwareComponent implements OnInit, OnDestroy {
  _showConditionHistoryModal: boolean = false;
  _openConditionHistory(): void { this._showConditionHistoryModal = true; }

  // #region Basic Variables
  _entity = 'asset';
  _page = 'assets';
  _permissionScope = 'assets';
  _theme: string;
  _currentUser: UserSummary;
  _active: boolean;
  _pageState: PageState;
  // _isLiveEnterprise: boolean;
  _model: IAssetDto;
  _language = 'en';
  _form: FormGroup;
  _gridFieldText: GridFieldTextSummary[];
  _invalidForm: boolean = false;
  _invalidSubmit: boolean;
  _isOs: boolean;
  _gridFieldAuth: GridFieldAuthorization[];
  _customForm: any;
  _isDialiog: boolean = false;
  _dialogComponentName: string;
  _dialogModel: any;
  _storagePath: string; //for the files library
  selectedobjectname: string = null;
  copyModelId: number;
  behavior: BehaviorSubject<GridFieldSummary[]>;
  // #endregion

  get lastPhysicalInventoryMonth(): string {
    return this._model?.lastPhysicalInventoryDate ? moment(this._model.lastPhysicalInventoryDate).format('MMMM') : '';
  }

  get lastPhysicalInventoryDay(): string {
    return this._model?.lastPhysicalInventoryDate ? moment(this._model.lastPhysicalInventoryDate).format('DD') : '';
  }

  get lastPhysicalInventoryYear(): string {
    return this._model?.lastPhysicalInventoryDate ? moment(this._model.lastPhysicalInventoryDate).format('YY') : '';
  }

  // #region Tab Containers
  _kendoTabStripPosition: TabPosition = 'left';
  _dataTabs: Array<FormTabSummary> = [];
  _dataTabsSelected: Array<FormTabSummary> = [];
  _userNewDataTabs: Array<FormTabSummary> = [];
  _customTabs: any;
  _tabDisabledIndexes: number[] = [];
  // #endregion

  // #region Window Properties
  _innerWidth: number;
  _hiddenWindow: string;
  _maximized: string;
  // #endregion

  // #region Popup Statuses
  _editFormActive: boolean;
  _orderingTabsForm: boolean = false;
  _deactivatedFieldsForm: boolean = false;
  _isEditForms: boolean = false;
  _selectedTabId: number;
  _selectedLabelId: number;
  _deactivatedFieldsGridData: any[] = [];
  // #endregion

  // #region Custom Fields / Edit Form
  _isCustomfieldsFormValue: boolean = false;
  _isAddTabFormValue: boolean = false;
  _isRenameLabel: boolean = false;
  _selectedLabelObjectCode: string;
  _isRenameFormValue: boolean = false;
  _isRenameCustomFormValue: boolean = false;
  _isMandatory: boolean = false;
  _custominvalidForm: boolean = false;
  reactivate: boolean = false;
  reactivateItem: any;
  requiredSystemTabs: any = [];
  _requiredFieldNames: any = [];
  _requiredFields: any = [];
  _additionalErrors: any = [];

  _customFields: CustomFieldSummary[][] = [];
  _customFieldsDictionary: { [key: string]: string } = {};
  _customFieldsValue: any[];
  _customValidation: any = [];
  _systemFields: GridFieldSummary[] = [];
  _editedSystemFields: any = [];
  // #endregion
  isLocationCodeReadonly: boolean = false;
  // #region HTML Elements References
  @ViewChild('mainWindow') kendoMainWindow: ElementRef;
  @ViewChild('kendo_tabstrip_wrapper') screenTabs: ElementRef;
  @ViewChild('kendo_tabstrip') public screenTabStrip: TabStripComponent;

  @ViewChild('KendoTabGeneral') public generalTab: TabStripComponent;
  @ViewChild('generalSortable') public generalSortable: SortableComponent;

  @ViewChild('kendo_assetnumber') kendoInputAssetNumber: ElementRef;
  @ViewChild('Kendo_Combobox_ModelNumber')
  kendoInputModelNumber: ComboBoxComponent;
  @ViewChild('kendo_AssetName') kendoInputAssetName: ElementRef;
  @ViewChild('note') note: NoteComponent;
  // #endregion

  // #region Current Page Specific
  _ModelMaxLength: number = 100;
  costPriceFormatType: FormatTypes = FormatTypes.CostandPriceFormat;
  quantityFormatType: FormatTypes = FormatTypes.QuantityFormat;
  _showFileNotes: boolean = false;
  _notesModel: IAssetDto;
  MinDate: Date = new Date(1930, 1, 1);
  isModelRequired: boolean;
  isAssetCheckedOut: boolean = false;
  //isUpcOnlineSetting:boolean = true; //>>>>>>>>>>>>>>>>>>temporarily
  //Transaction Field Editor
  _showTransactionFieldEditor: boolean = false;
  _transactionsFieldEditorObjectCode: string = '';

  ////// Model Number ////////
  _assetNumber = new FormControl();
  modelsData: IAssetModelDto[] = [];
  _modelsAuth: GridFieldAuthorization;
  _modelsDD = new FormControl();
  _modelListEnabled: boolean = false;
  _modelGridFields: GridFieldSummary[] = [];
  _modelPageName: string = 'asset-models';
  _selectedModel: number;
  ///// model type /////
  _modelTypeId: number;
  _modelTypeInfo: IAssetModelDto;
  _modelTypeFilesPics: assetFile[] = [];
  _modelTypeFilesDoc: assetFile[] = [];

  ////// assetName ////////
  _assetName = new FormControl();

  ////// AcquisitionType ////////
  acquisitionTypesData: IAcquisitionTypeDto[] = [];
  _acquisitionTypesDD = new FormControl();
  _acquisitionTypeId: number;

  ////// DepreciationMethod ////////
  depreciationMethodsData: IDepreciationMethodDto[] = [];
  _depreciationMethodsDD = new FormControl();
  _depreciationMethodId: number;
  ////// Condition ////////
  _conditionCode = new FormControl();
  conditionsData: ConditionDto[] = [];
  _conditionsAuth: GridFieldAuthorization;
  _conditionsDD = new FormControl();
  _conditionsListEnabled: boolean = false;
  _conditionsGridFields: GridFieldSummary[] = [];
  _conditionsPageName: string = 'conditions';
  _selectedCondition: number;

  ////// Statuses ////////
  _statusesCode = new FormControl();
  statusesData: StatusDto[] = [];
  _statusesAuth: GridFieldAuthorization;
  _statusesDD = new FormControl();
  _statusesListEnabled: boolean = false;
  _statusesGridFields: GridFieldSummary[] = [];
  _statusesPageName: string = 'statuses';
  _selectedStatus: number;

  ////// Person ////////
  _personsCode = new FormControl();
  personsData: PersonSummary[] = [];
  _personsAuth: GridFieldAuthorization;
  _personsDD = new FormControl();
  _personsListEnabled: boolean = false;
  _personsGridFields: GridFieldSummary[] = [];
  _personsPageName: string = 'persons';
  _selectedPerson: string;

  /////// Location Common //////
  locationsData: LocationSummary[] = [];
  _locationsAuth: GridFieldAuthorization;
  _locationsGridFields: GridFieldSummary[] = [];
  _locationsPageName: string = 'locations';
  _activeLocationItem: CurrentSelectedAssetLocation =
    CurrentSelectedAssetLocation.homeLocation;
  _locationsListEnabled: boolean = false;
  _selectedLocation: number;
  ////// Home Location ////////
  _homeLocationsCode = new FormControl();
  _homeLocationsDD = new FormControl();
  ////// Location ////////
  _locationsCode = new FormControl();
  _locationsDD = new FormControl();

  /////// Supplier Common //////
  vendorsData: VendorSummary[] = [];
  _vendorsAuth: GridFieldAuthorization;
  _vendorsGridFields: GridFieldSummary[] = [];
  _vendorsPageName: string = 'vendors';
  _activeVendorItem: CurrentSelectedSupplier =
    CurrentSelectedSupplier.purchaseSupplier;
  _supplierListEnabled: boolean = false;
  _selectedSupplier: number;
  ////// Maintenance Supplier ////////
  _maintananceSupplierCode = new FormControl();
  _maintananceSuppliersDD = new FormControl();
  ////// Purchase Supplier ////////
  _purchaseSupplierCode = new FormControl();
  _purchaseSuppliersDD = new FormControl();
  ////// Lease Supplier ////////
  _leaseSupplierCode = new FormControl();
  _leaseSuppliersDD = new FormControl();

  ////// Asset Assignees ////////
  _selectedAssigneesKeys: number[] = [];
  _selectedAssignees: any[];
  _assetAssignees: IAssetAssignees[] = [];
  _assetAssignees_General: IAssetAssignees[] = [];
  _assetAssignees_Software: IAssetAssignees[] = [];
  _assetAssignees_Computer: IAssetAssignees[] = [];
  _showAssigneesGrid: boolean = false;
  _assetsGridFields: GridFieldSummary[];
  _selectedObjectAssets: any[];
  _assigneeType: AssigneesType = AssigneesType.general;
  assetAssigneesGridExecludedFields: string[] = [];

  ////// Funding Source ////////
  _fundingSourcesCode = new FormControl();
  fundingSourcesData: FundingSourceDto[] = [];
  _fundingSourcesAuth: GridFieldAuthorization;
  _fundingSourcesDD = new FormControl();
  _fundingSourcesListEnabled: boolean = false;
  _fundingSourcesGridFields: GridFieldSummary[] = [];
  _fundingSourcesPageName: string = 'fundingsource';
  _selectedFundingSource: number;

  fundingEnabled: boolean = false;

  //Date Compare
  _dateSource: string | 'start' | 'end';
  // #region Videos/Documents/Pictures
  _filesPageName: string = 'files';
  _documentsPageName: string = 'documents';
  _videosPageName: string = 'videos';
  _imageSearchControl = new FormControl();
  _documentSearchControl = new FormControl();
  _videoSearchControl = new FormControl();
  _showImagesGrid: boolean = false;
  _showDocumentGrid: boolean = false;
  _showVideoGrid: boolean = false;
  _imageSearchResult: string;
  _documentSearchResult: string;
  _videoSearchResult: string;
  _filesGridFields: GridFieldSummary[];
  _documentsGridFields: GridFieldSummary[];
  _videosGridFields: GridFieldSummary[];
  _filesGridFieldsSub!: Subscription;
  _selectedImages: any[] = [];
  _selectedImagesKeys: number[] = [];
  _selectedDocuments: any[] = [];
  _selectedDocumentsKeys: number[] = [];
  _selectedVideos: any[] = [];
  _selectedVideosKeys: number[] = [];
  _assetFilesPics: assetFile[] = [];
  _assetFilesDoc: assetFile[] = [];
  _assetFilesVideo: assetFile[] = [];
  _selectedObjectImages: any[] = [];
  _selectedObjectDocuments: any[] = [];
  _selectedObjectVideos: any[] = [];
  _selectedUpsertImagesKeys: number[] = [];
  // #endregion

  // #endregion

  // #region Asset History
  _historyPageName: string = 'asset-all-history';
  _historyGridFields: GridFieldSummary[] = [];

  // #endregion

  // #region Scheduled Maintenance & Maintenance History
  _scheduledMaintenancePageName: string = PageNames.ScheduleMaintenance;
  _scheduledMaintenanceGridFields: GridFieldSummary[] = [];
  _scheduledMaintenanceList: any[] = [];
  _scheduledMaintenanceLoading: boolean = false;
  _performedMaintenanceHistoryPageName: string = PageNames.PerformedMaintenanceHistory;
  _performedMaintenanceHistoryGridFields: GridFieldSummary[] = [];
  _performedMaintenanceHistoryList: any[] = [];
  _performedMaintenanceHistoryLoading: boolean = false;
  showMaintenanceDocumentsWindow: boolean = false;
  maintenanceDocumentsData: any[] = [];
  // #endregion
  // #region Enterprise Data
  _addClassActive: boolean = false;
  _selectedAccounts: any[] = [];
  declare _tabEnabled: boolean;
  // #endregion

  //Sites Drop
  _siteAuth: GridFieldAuthorization;
  _selectSitesGridActive: boolean;
  _sitesPageName: string = 'sites';
  _sitesGridFields: GridFieldSummary[];
  _selectedsite: number;
  _sitesDD = new FormControl();

  constructor(
    private _accountsService: AccountsService,
    private _fb: FormBuilder,
    private sitesService: MultiSiteService,
    private _assetService: AssetService,
    private _assetModelService: AssetModelService,
    private _acquisitionTypeService: AcquisitionTypeService,
    private _depreciationMethodService: DepreciationMethodService,
    private _conditionsService: ConditionsService,
    private _statusService: StatusesService,
    private _usersService: UsersService,
    private _locationsService: LocationsService,
    private _vendorsService: VendorsService,
    private _fundingSourceService: FundingSourceService,
    private _fundingSourceSettingService: FundingSourceSettingsService,
    private _assetModelSettingsService: AssetModelSettingsService,
    private _assetAllHistoryService: AssetAllHistoryService,
    private _scheduleMaintenanceService: ScheduleMaintenanceService,
    private _performMaintenanceService: PerformMaintenanceService,

    private _gridLayoutManagerService: GridLayoutManagerService,
    private _sharedService: SharedService,
    private _securityService: SecurityService,
    private _themeManager: ThemeManagerService,
    private _customFieldsService: CustomFieldsService,
    private _gridFields: GridFieldsService,
    private _barcodeGeneratorsService: BarcodeGeneratorsService,
    private _categoriesService: CategoriesService,
    private _layoutManager: LayoutManagerService,
    private _dataService: GDataService,
    private _filesService: FilesService,
    private _modelClassService: ModelClassService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private _layoutComponent: LayoutComponent,
  ) {
    super();
  }
  private zoneStableSub?: Subscription;

  ngOnInit(): void {
    let stableCount = 0;
    setTimeout(() => {
      this.zoneStableSub = this.ngZone.onStable.subscribe(() => {
        this.cdr.detectChanges();
        setTimeout(() => {
        }, 1000);
        stableCount++;
        if (stableCount > 13) {
          setTimeout(() => {
            this.zoneStableSub?.unsubscribe();
          }, 2000);
        } else if (stableCount > 10) {
          setTimeout(() => {
            this.zoneStableSub?.unsubscribe();
          }, 2500);
        } else if (stableCount > 7) {
          setTimeout(() => {
            this.zoneStableSub?.unsubscribe();
          }, 3000);
        }
      });
    }, 1000);

    this._themeManager.themeChange.subscribe((data) => {
      this._theme = data;
    });
    if (window.navigator.userAgent.indexOf('Mac') != -1) {
      this._isOs = true;
    }

    this._storagePath =
      EnvironmentLoaderService.envConfig.enterpriseApiLocal + '/';
    this._layoutManager.applyOverly();
    this._currentUser = this._securityService.currentUser;
    this._getdata();
    this._getAccountIds();
    this._getModelSettings();
    this.getAuth();
    this.getSystemFieldsAndCustomFields();
    this._getInactiveFields();
    this.loadLabels();
    this._initForm();
    this.getFormTabs();

    this._checkMultiSitesLicense();
    this.getSitesAuth();
    // Tab Position
    let windowSize = window.innerWidth;
    if (windowSize <= 991) {
      this._kendoTabStripPosition = 'top';
    } else {
      this._kendoTabStripPosition = 'left';
    }
    if (this._pageState == PageState.ADD) {
      setTimeout(() => {
        this.kendoInputAssetNumber?.nativeElement?.focus();
      }, 2000);
    }
    this._dataService.dataTransferObservable.subscribe((str) => {
      if (str.isAttachment == true) {
        if (str?.response && str.component == 'files') {
          this._addPicture(str);
        }
        if (str?.response && str.component == 'documents') {
          this._addDocument(str);
        }
        if (str?.response && str.component == 'videos') {
          this._addVideo(str);
        }
      } else {
        if (str?.response && str.component == 'asset-models') {
          this._onCloseAddAssetModel(str);
        }
        if (str?.response && str.component == 'conditions') {
          this._onCloseAddCondition(str);
        }
        if (str?.response && str.component == 'statuses') {
          this._onCloseAddStatuses(str);
        }
        if (str?.response && str.component == 'users') {
          this._onCloseAddPersons(str);
        }
        if (str?.response && str.component == 'locations') {
          this._onCloseAddLocation(str);
        }
        if (str?.response && str.component == 'vendors') {
          this._onCloseAddVendor(str);
        }
        if (str?.response && str.component == 'fundingsource') {
          this._onCloseAddFundingSources(str);
        }
        if (str?.response && str.component == 'sites') {
          this._onCloseSite(str);
        }
      }
      this._isDialiog = false;
    });
    /* this._form.controls.modelNumber.valueChanges.subscribe((val:IAssetModelDto) => {
      this.setModelTypeId(val);
    }); */
    this._form.controls.acquisitionType.valueChanges.subscribe(
      (val: IAcquisitionTypeDto) => {
        this.setAcquisitionTypeId(val);
      },
    );
    this._form.controls.purchaseCost.valueChanges.subscribe((cost: any) => {
      const depTaken =
        this._form.controls.depreciationTaken?.value ??
        this._model?.depreciationTaken ??
        0;
      if (cost !== null && cost !== undefined && cost !== '') {
        const costNum = Number(cost);
        if (!isNaN(costNum)) {
          const calculatedBookValue = Math.max(
            0,
            costNum - Number(depTaken || 0),
          );
          this._form.controls.bookValue?.setValue(calculatedBookValue, {
            emitEvent: false,
          });
        } else {
          this._form.controls.bookValue?.setValue(null, { emitEvent: false });
        }
      } else {
        this._form.controls.bookValue?.setValue(null, { emitEvent: false });
      }
    });
    /* this._form.controls.leaseStartDate.valueChanges.subscribe(() => {
      this.compareLeaseDates();
    });
    this._form.controls.leaseEndDate.valueChanges.subscribe(() => {
      this.compareLeaseDates();
    });
    this._form.controls.maintananceContractStartDate.valueChanges.subscribe(() => {
      this.compareMaintananceContractDates();
    });
    this._form.controls.maintananceContractEndDate.valueChanges.subscribe(() => {
      this.compareMaintananceContractDates();
    });
    this._form.controls.warrantyStartDate.valueChanges.subscribe(() => {
      this.compareWarrantyDates();
    });
    this._form.controls.warrantyEndDate.valueChanges.subscribe(() => {
      this.compareWarrantyDates();
    }); */
    this._setGridFieldBehaviour();
    //if (!this._currentUser.isEnterprise)
    this.getAssetModelsDD();
    this.getAssetModelAuth();
    this.getAcquisitionTypesDD();
    this.getDepreciationMethodsDD();
    this.getConditionsDD();
    this.getConditionsAuth();
    this.getStatusesDD();
    this.getStatusesAuth();
    this.getPersonsDD();
    this.getPersonsAuth();
    this.getLocationsDD();
    this.getLocationsAuth();
    this.getVendorsDD();
    this.getVendorsAuth();
    this.getFundingSourceSetting();
    this.getFundingsourcesAuth();
    this.getFundingsourcesDD();
    this._getAllHistoryGridFields();
    this._getScheduledMaintenanceGridFields();
    this._getPerformedMaintenanceHistoryGridFields();
    this.loadScheduledMaintenanceData();
    this.loadPerformedMaintenanceHistoryData();
    if (this._sharedService?.model?.transactionAssetNumber) {
      this._form.patchValue({
        assetNumber: this._sharedService?.model?.transactionAssetNumber,
      });
    }
  }
  ngOnDestroy(): void {
    if (this._filesGridFieldsSub) this._filesGridFieldsSub.unsubscribe();
  }
  // #region Initial Loading

  _setGridFieldBehaviour() {
    this.behavior = this._gridLayoutManagerService.subscribeToColumnsChanges2();

    this.behavior.subscribe((value) => {
      if (value != undefined && value != null) {
        let pagename = value.map((a: any) => a.page).shift();
        let Gridresualt = value.filter(
          (a: any) => a.selected === true && a.page === pagename,
        );
        switch (pagename) {
          case 'files': {
            this._filesGridFields = Gridresualt;
            break;
          }
          case 'documents': {
            this._documentsGridFields = Gridresualt;
            break;
          }
          case 'videos': {
            this._videosGridFields = Gridresualt;
            break;
          }
          case 'asset-models': {
            this._modelGridFields = Gridresualt;
            break;
          }
          case 'conditions': {
            this._conditionsGridFields = Gridresualt;
            break;
          }
          case 'statuses': {
            this._statusesGridFields = Gridresualt;
            break;
          }
          case 'users': {
            this._personsGridFields = Gridresualt;
            break;
          }
          case 'locations': {
            this._locationsGridFields = Gridresualt;
            break;
          }
          case 'vendors': {
            this._vendorsGridFields = Gridresualt;
            break;
          }
          case 'assets': {
            this._assetsGridFields = Gridresualt;
            break;
          }
          case 'fundingsource': {
            this._fundingSourcesGridFields = Gridresualt;
            break;
          }
          case '_historyGridFields': {
            this._historyGridFields = Gridresualt;
            break;
          }
          case 'schedule-maintenance': {
            this._scheduledMaintenanceGridFields = Gridresualt;
            break;
          }
          case 'performedMaintenanceHistory': {
            this._performedMaintenanceHistoryGridFields = Gridresualt;
            break;
          }
          case 'sites': {
            this._sitesGridFields = Gridresualt;
            break;
          }
          default:
            break;
        }
      }
    });
  }

  _getdata(): void {
    this._model =
      this._sharedService.model && this._sharedService.model.model
        ? this._sharedService.model.model
        : null;
    this._pageState =
      this._sharedService.model && this._sharedService.model.pageState
        ? this._sharedService.model.pageState
        : null;
    this._active = true;
    this._assetFilesPics = [];
    this._assetFilesDoc = [];
    if (this._model?.assetFiles) {
      this._model.assetFiles.forEach((element: any) => {
        if (element.file?.type.includes('image')) {
          this._assetFilesPics.push(element);
        } else if (element.file?.type.includes('application')) {
          this._assetFilesDoc.push(element);
        } else if (element.file?.type.includes('video')) {
          this._assetFilesVideo.push(element);
        }
      });
    }

    if (this._model?.assetAssignees) {
      this._assetAssignees = [...this._model?.assetAssignees];
      this._assetAssignees_General = this._assetAssignees.filter(
        (f) => f.assigneesType.toString().toLowerCase() == 'general',
      );
      this._assetAssignees_Software = this._assetAssignees.filter(
        (f) => f.assigneesType.toString().toLowerCase() == 'software',
      );
      this._assetAssignees_Computer = this._assetAssignees.filter(
        (f) => f.assigneesType.toString().toLowerCase() == 'computer',
      );
    }

    if (this._pageState == PageState.COPY) {
      this.copyModelId = this._model.id; // for loading custom fields data if there is any
    }

    if (
      (this._pageState === PageState.ADD ||
        this._pageState == PageState.COPY) &&
      this._model &&
      this._model.id > 0
    ) {
      this._model.id = 0;
      this._model.assetModelRelatedFields.id = 0;
    }

    if (this._pageState == PageState.ADD) {
      this._model.id = 0;
      this._model.assetModelRelatedFields = <IAssetModelRelatedFields>{};
      this._model.assetModelRelatedFields.id = 0;
    }

    if (
      this._pageState === PageState.ADD ||
      this._pageState == PageState.COPY
    ) {
      if (this._model) {
        this._model.depreciationTaken = 0;
        this._model.depreciationTakenDate = null;
        this._model.depreciationDate = null;
        this._model.bookValue = this._model.cost ?? null;
      }
    } else if (
      this._pageState == PageState.EDIT ||
      this._pageState == PageState.VIEW
    ) {
      if (this._model) {
        if (
          this._model.depreciationTaken === undefined ||
          this._model.depreciationTaken === null
        ) {
          if (this._model.id > 0) {
            this._model.depreciationTaken = 0;
          }
        }
        if (
          !this._model.depreciationTakenDate &&
          this._model.depreciationDate
        ) {
          this._model.depreciationTakenDate = this._model.depreciationDate;
        }
        if (
          this._model.bookValue === undefined ||
          this._model.bookValue === null
        ) {
          if (this._model.cost !== undefined && this._model.cost !== null) {
            this._model.bookValue = Math.max(
              0,
              (this._model.cost ?? 0) - (this._model.depreciationTaken ?? 0),
            );
          }
        }
      }
    }

    if (
      this._pageState == PageState.EDIT ||
      this._pageState == PageState.VIEW
    ) {
      this.isLocationCodeReadonly = true;
      this._selectedAccounts = (this._model?.assetSites ?? []).map((item: any) => ({
        siteId: item.site?.id ?? item.siteId,
        accountId: item.account?.id ?? item.accountId,
      }));
      this._selectedAccountIds = this._selectedAccounts.map((a: any) => +a.accountId);
    }

    if (this._currentUser.isEnterprise) {
      this._tabEnabled = this._selectedAccounts.length > 0;
    } else {
      this._tabEnabled = true;
    }
    //this._tabEnabled = this._selectedAccounts.length > 0;
    if (this._model?.model != undefined) {
      this.setModelTypeId(this._model?.model);
    }
    if (this._model?.acquisitionType != undefined) {
      this.setAcquisitionTypeId(this._model?.acquisitionType);
    }
    this.isAssetCheckedOut = this._model?.checkedout ?? false;
    this.loadScheduledMaintenanceData();
    this.loadPerformedMaintenanceHistoryData();
  }
  _getModelSettings() {
    this._assetModelSettingsService
      .getassetModelSettings()

      .subscribe((data: IAssetModelSettingsDto) => {
        this.isModelRequired = data.isRequired;

        if (this.isModelRequired == true) {
          this._form.controls.modelNumber.addValidators(Validators.required);
        }
      });
  }

  _getAccountIds() {
    super._getAccountIds(this._model?.assetSites);
  }

  async loadLabels() {
    this._gridFields
      .getGridFieldTextLabelByPage(this._page, this._language)

      .subscribe((data: GridFieldTextSummary[]) => {
        this._gridFieldText = data;
        //this._initForm();
      });
  }

  getFundingSourceSetting() {
    this._fundingSourceSettingService
      .getFundingSourceSettings()

      .subscribe((data) => {
        if (data != undefined && data != null)
          this.fundingEnabled = data.enabled;
      });
  }

  _checkMultiSitesLicense() {
    super._checkMultiSitesLicense(this._securityService, () => this._loadSitesDD());
  }

  _initForm(): void {
    this._form = this._fb.group({
      assetNumber: [this._model.assetNumber, Validators.required],
      site: [
        this._model?.site,
        this.getLabel('LBL_SiteCode')?.isRequired ? Validators.required : null,
      ],
      serialNumber: [
        this._model?.serialNumber,
        this.getLabel('LBL_SerialNumber')?.isRequired
          ? Validators.required
          : null,
      ],
      serialNumberIsUnique: [
        this._model?.serialNumberIsUnique,
        this.getLabel('LBL_SerialNumberIsUnique')?.isRequired
          ? Validators.required
          : null,
      ],
      serialNumberIsInsured: [
        this._model?.serialNumberIsInsured,
        this.getLabel('LBL_SerialNumberIsInsured')?.isRequired
          ? Validators.required
          : null,
      ],
      assetName: [
        this._model?.assetName,
        this.getLabel('LBL_AssetName')?.isRequired ? Validators.required : null,
      ],
      acquisitionType: [
        this._model?.acquisitionType,
        this.getLabel('LBL_AcquisitionType')?.isRequired
          ? Validators.required
          : null,
      ],
      availableForCheckout: [
        this._model?.availableForCheckout,
        this.getLabel('LBL_AvailableForCheckout')?.isRequired
          ? Validators.required
          : null,
      ],
      availableForCustodyTransfer: [
        this._model?.availableForCustodyTransfere,
        this.getLabel('LBL_AvailableForCustodyTransfer')?.isRequired
          ? Validators.required
          : null,
      ],
      modelNumber: [
        this._model?.model,
        this.getLabel('LBL_AssetModelNumber')?.isRequired ||
          this.isModelRequired == true
          ? Validators.required
          : null,
      ],
      conditionCode: [
        this._model?.condition,
        this.getLabel('LBL_Condition')?.isRequired ? Validators.required : null,
      ],
      assetStatusesCode: [
        this._model?.assetStatus,
        this.getLabel('LBL_AssetStatus')?.isRequired
          ? Validators.required
          : null,
      ],
      personCode: [
        this._model?.person,
        this.getLabel('LBL_AssetPersonID')?.isRequired
          ? Validators.required
          : null,
      ],
      homeLocationCode: [
        this._model?.homeLocation,
        this.getLabel('LBL_HomeLocation')?.isRequired
          ? Validators.required
          : null,
      ],
      locationCode: [
        this._model?.location,
        this.getLabel('LBL_AssetLocation')?.isRequired
          ? Validators.required
          : null,
      ],
      description: [
        this._model?.description,
        this.getLabel('LBL_Description')?.isRequired
          ? Validators.required
          : null,
      ],
      manufacturer: [
        this._model?.manufacturer,
        this.getLabel('LBL_Manufacturer')?.isRequired
          ? Validators.required
          : null,
      ],
      warrantyNumber: [
        this._model?.warrantyNumber,
        this.getLabel('LBL_WarrantyNumber')?.isRequired
          ? Validators.required
          : null,
      ],
      warrantyStartDate: [
        parseDate(this._model?.warrantyStartDate),
        this.getLabel('LBL_WarrantyStartDate')?.isRequired
          ? Validators.required
          : null,
      ],
      endOfLife: [
        parseDate(this._model?.endOfLife),
        this.getLabel('LBL_EndOfLife')?.isRequired ? Validators.required : null,
      ],
      endOfSupport: [
        parseDate(this._model?.endOfSupport),
        this.getLabel('LBL_EndOfSupport')?.isRequired
          ? Validators.required
          : null,
      ],
      warrantyEndDate: [
        parseDate(this._model?.warrantyEndDate),
        this.getLabel('LBL_WarrantyEndDate')?.isRequired
          ? Validators.required
          : null,
      ],
      maintananceSupplierCode: [
        this._model?.maintananceSupplier,
        this.getLabel('LBL_MaintananceSupplier')?.isRequired
          ? Validators.required
          : null,
      ],
      maintananceContractCost: [
        this._model?.maintananceContractCost,
        this.getLabel('LBL_MaintananceContractCost')?.isRequired
          ? Validators.required
          : null,
      ],
      maintananceContractStartDate: [
        parseDate(this._model?.maintananceContractStartDate),
        this.getLabel('LBL_MaintananceContractStartDate')?.isRequired
          ? Validators.required
          : null,
      ],
      maintananceContractEndDate: [
        parseDate(this._model?.maintananceContractEndDate),
        this.getLabel('LBL_MaintananceContractEndDate')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerCPU: [
        this._model?.assetModelRelatedFields?.computerCPU,
        this.getLabel('LBL_ModelComputerCPU')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerCPUSpeed: [
        this._model?.assetModelRelatedFields?.computerCPUSpeed,
        this.getLabel('LBL_ModelComputerCPUSpeed')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerHardDriveSize: [
        this._model?.assetModelRelatedFields?.computerHardDriveSize,
        this.getLabel('LBL_ModelComputerHardDriveSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerIPAddress: [
        this._model?.assetModelRelatedFields?.computerIPAddress,
        this.getLabel('LBL_ModelComputerIPAddress')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerOS: [
        this._model?.assetModelRelatedFields?.computerOS,
        this.getLabel('LBL_ModelComputerOS')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerBIOS: [
        this._model?.assetModelRelatedFields?.computerBIOS,
        this.getLabel('LBL_ModelComputerBIOS')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerRAM: [
        this._model?.assetModelRelatedFields?.computerRAM,
        this.getLabel('LBL_ModelComputerRAM')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerVideoCard: [
        this._model?.assetModelRelatedFields?.computerVideoCard,
        this.getLabel('LBL_ModelComputerVideoCard')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelComputerMACAddress: [
        this._model?.assetModelRelatedFields?.computerMACAddress,
        this.getLabel('LBL_ModelComputerMACAddress')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelSoftwareVersion: [
        this._model?.assetModelRelatedFields?.softwareVersion,
        this.getLabel('LBL_ModelSoftwareVersion')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelSoftwareLicenses: [
        this._model?.assetModelRelatedFields?.softwareLicenses,
        this.getLabel('LBL_ModelSoftwareLicenses')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelFurnitureSize: [
        this._model?.assetModelRelatedFields?.furnitureSize,
        this.getLabel('LBL_ModelFurnitureSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelFurnitureColor: [
        this._model?.assetModelRelatedFields?.furnitureColor,
        this.getLabel('LBL_ModelFurnitureColor')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelFurnitureHeight: [
        this._model?.assetModelRelatedFields?.furnitureHeight,
        this.getLabel('LBL_ModelFurnitureHeight')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelFurnitureLength: [
        this._model?.assetModelRelatedFields?.furnitureLength,
        this.getLabel('LBL_ModelFurnitureLength')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelFurnitureWidth: [
        this._model?.assetModelRelatedFields?.furnitureWidth,
        this.getLabel('LBL_ModelFurnitureWidth')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelMonitorResolution: [
        this._model?.assetModelRelatedFields?.monitorResolution,
        this.getLabel('LBL_ModelMonitorResolution')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelMonitorScreenSize: [
        this._model?.assetModelRelatedFields?.monitorScreenSize,
        this.getLabel('LBL_ModelMonitorScreenSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelVehicleColor: [
        this._model?.assetModelRelatedFields?.vehicleColor,
        this.getLabel('LBL_ModelVehicleColor')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelVehicleLicensePlate: [
        this._model?.assetModelRelatedFields?.vehicleLicensePlate,
        this.getLabel('LBL_ModelVehicleLicensePlate')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelVehicleVIN: [
        this._model?.assetModelRelatedFields?.vehicleVIN,
        this.getLabel('LBL_ModelVehicleVIN')?.isRequired
          ? Validators.required
          : null,
      ],
      rModelVehicleMileage: [
        this._model?.assetModelRelatedFields?.vehicleMileage,
        this.getLabel('LBL_ModelVehicleMileage')?.isRequired
          ? Validators.required
          : null,
      ],
      rFaxResolution: [
        this._model?.assetModelRelatedFields?.faxResolution,
        this.getLabel('LBL_FaxResolution')?.isRequired
          ? Validators.required
          : null,
      ],
      rFaxScannerType: [
        this._model?.assetModelRelatedFields?.faxScannerType,
        this.getLabel('LBL_FaxScannerType')?.isRequired
          ? Validators.required
          : null,
      ],
      rFaxType: [
        this._model?.assetModelRelatedFields?.faxType,
        this.getLabel('LBL_FaxType')?.isRequired ? Validators.required : null,
      ],
      rFaxPrintTechnology: [
        this._model?.assetModelRelatedFields?.faxPrintTechnology,
        this.getLabel('LBL_FaxPrintTechnology')?.isRequired
          ? Validators.required
          : null,
      ],
      rFaxScanningWidth: [
        this._model?.assetModelRelatedFields?.faxScanningWidth,
        this.getLabel('LBL_FaxScanningWidth')?.isRequired
          ? Validators.required
          : null,
      ],
      rFaxMemorySize: [
        this._model?.assetModelRelatedFields?.faxMemorySize,
        this.getLabel('LBL_FaxMemorySize')?.isRequired
          ? Validators.required
          : null,
      ],
      rTabletsScreenSize: [
        this._model?.assetModelRelatedFields?.tabletsScreenSize,
        this.getLabel('LBL_TabletsScreenSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rTabletsScreenResolution: [
        this._model?.assetModelRelatedFields?.tabletsScreenResolution,
        this.getLabel('LBL_TabletsScreenResolution')?.isRequired
          ? Validators.required
          : null,
      ],
      rTabletsStorage: [
        this._model?.assetModelRelatedFields?.tabletsStorage,
        this.getLabel('LBL_TabletsStorage')?.isRequired
          ? Validators.required
          : null,
      ],
      rTabletsConnectivity: [
        this._model?.assetModelRelatedFields?.tabletsConnectivity,
        this.getLabel('LBL_TabletsConnectivity')?.isRequired
          ? Validators.required
          : null,
      ],
      rTabletsBatteryLife: [
        this._model?.assetModelRelatedFields?.tabletsBatteryLife,
        this.getLabel('LBL_TabletsBatteryLife')?.isRequired
          ? Validators.required
          : null,
      ],
      rTabletsCamera: [
        this._model?.assetModelRelatedFields?.tabletsCamera,
        this.getLabel('LBL_TabletsCamera')?.isRequired
          ? Validators.required
          : null,
      ],
      rMobileDevicesRAM: [
        this._model?.assetModelRelatedFields?.mobileDevicesRAM,
        this.getLabel('LBL_MobileDevicesRAM')?.isRequired
          ? Validators.required
          : null,
      ],
      rMobileDevicesStorage: [
        this._model?.assetModelRelatedFields?.mobileDevicesStorage,
        this.getLabel('LBL_MobileDevicesStorage')?.isRequired
          ? Validators.required
          : null,
      ],
      rMobileDevicesBattery: [
        this._model?.assetModelRelatedFields?.mobileDevicesBattery,
        this.getLabel('LBL_MobileDevicesBattery')?.isRequired
          ? Validators.required
          : null,
      ],
      rMobileDevicesOperatingSystem: [
        this._model?.assetModelRelatedFields?.mobileDevicesOperatingSystem,
        this.getLabel('LBL_MobileDevicesOperatingSystem')?.isRequired
          ? Validators.required
          : null,
      ],
      rMobileDevicesScreenSize: [
        this._model?.assetModelRelatedFields?.mobileDevicesScreenSize,
        this.getLabel('LBL_MobileDevicesScreenSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rMobileDevicesCamera: [
        this._model?.assetModelRelatedFields?.mobileDevicesCamera,
        this.getLabel('LBL_MobileDevicesCamera')?.isRequired
          ? Validators.required
          : null,
      ],
      rMobileDevicesDisplayQuality: [
        this._model?.assetModelRelatedFields?.mobileDevicesDisplayQuality,
        this.getLabel('LBL_MobileDevicesDisplayQuality')?.isRequired
          ? Validators.required
          : null,
      ],
      rSmartWatchesDimensions: [
        this._model?.assetModelRelatedFields?.smartWatchesDimensions,
        this.getLabel('LBL_SmartWatchesDimensions')?.isRequired
          ? Validators.required
          : null,
      ],
      rSmartWatchesWeight: [
        this._model?.assetModelRelatedFields?.smartWatchesWeight,
        this.getLabel('LBL_SmartWatchesWeight')?.isRequired
          ? Validators.required
          : null,
      ],
      rSmartWatchesBuild: [
        this._model?.assetModelRelatedFields?.smartWatchesBuild,
        this.getLabel('LBL_SmartWatchesBuild')?.isRequired
          ? Validators.required
          : null,
      ],
      rSmartWatchesSIM: [
        this._model?.assetModelRelatedFields?.smartWatchesSIM,
        this.getLabel('LBL_SmartWatchesSIM')?.isRequired
          ? Validators.required
          : null,
      ],
      rCamerasNumberOfMegapixels: [
        this._model?.assetModelRelatedFields?.camerasNumberOfMegapixels,
        this.getLabel('LBL_CamerasNumberOfMegapixels')?.isRequired
          ? Validators.required
          : null,
      ],
      rCamerasAperture: [
        this._model?.assetModelRelatedFields?.camerasAperture,
        this.getLabel('LBL_CamerasAperture')?.isRequired
          ? Validators.required
          : null,
      ],
      rCamerasFocalLength: [
        this._model?.assetModelRelatedFields?.camerasFocalLength,
        this.getLabel('LBL_CamerasFocalLength')?.isRequired
          ? Validators.required
          : null,
      ],
      rCamerasSensorSize: [
        this._model?.assetModelRelatedFields?.camerasSensorSize,
        this.getLabel('LBL_CamerasSensorSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rCamerasZoomType: [
        this._model?.assetModelRelatedFields?.camerasZoomType,
        this.getLabel('LBL_CamerasZoomType')?.isRequired
          ? Validators.required
          : null,
      ],
      rCamerasMethodsOfStabilization: [
        this._model?.assetModelRelatedFields?.camerasMethodsOfStabilization,
        this.getLabel('LBL_CamerasMethodsOfStabilization')?.isRequired
          ? Validators.required
          : null,
      ],
      rCamerasFocusingSystem: [
        this._model?.assetModelRelatedFields?.camerasFocusingSystem,
        this.getLabel('LBL_CamerasFocusingSystem')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsScreenSize: [
        this._model?.assetModelRelatedFields?.tVsScreenSize,
        this.getLabel('LBL_TVsScreenSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsResolution: [
        this._model?.assetModelRelatedFields?.tVsResolution,
        this.getLabel('LBL_TVsResolution')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsInputLag: [
        this._model?.assetModelRelatedFields?.tVsInputLag,
        this.getLabel('LBL_TVsInputLag')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsRefreshRate: [
        this._model?.assetModelRelatedFields?.tVsRefreshRate,
        this.getLabel('LBL_TVsRefreshRate')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsLED: [
        this._model?.assetModelRelatedFields?.tVsLED,
        this.getLabel('LBL_TVsLED')?.isRequired ? Validators.required : null,
      ],
      rTVsOLEDAndPlasma: [
        this._model?.assetModelRelatedFields?.tVsOLEDAndPlasma,
        this.getLabel('LBL_TVsOLEDAndPlasma')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsHDR: [
        this._model?.assetModelRelatedFields?.tVsHDR,
        this.getLabel('LBL_TVsHDR')?.isRequired ? Validators.required : null,
      ],
      rTVsLocalDimming: [
        this._model?.assetModelRelatedFields?.tVsLocalDimming,
        this.getLabel('LBL_TVsLocalDimming')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsViewingAngle: [
        this._model?.assetModelRelatedFields?.tVsViewingAngle,
        this.getLabel('LBL_TVsViewingAngles')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsInternetConnectivity: [
        this._model?.assetModelRelatedFields?.tVsInternetConnectivity,
        this.getLabel('LBL_TVsInternetConnectivity')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsAppStore: [
        this._model?.assetModelRelatedFields?.tVsAppStore,
        this.getLabel('LBL_TVsAppStore')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsVoiceControl: [
        this._model?.assetModelRelatedFields?.tVsVoiceControl,
        this.getLabel('LBL_TVsVoiceControl')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsScreenMirroring: [
        this._model?.assetModelRelatedFields?.tVsScreenMirroring,
        this.getLabel('LBL_TVsScreenMirroring')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsGaming: [
        this._model?.assetModelRelatedFields?.tVsGaming,
        this.getLabel('LBL_TVsGaming')?.isRequired ? Validators.required : null,
      ],
      rTVsPorts: [
        this._model?.assetModelRelatedFields?.tVsPorts,
        this.getLabel('LBL_TVsPorts')?.isRequired ? Validators.required : null,
      ],
      rTVsPictureQuality: [
        this._model?.assetModelRelatedFields?.tVsPictureQuality,
        this.getLabel('LBL_TVsPictureQuality')?.isRequired
          ? Validators.required
          : null,
      ],
      rTVsSoundQuality: [
        this._model?.assetModelRelatedFields?.tVsSoundQuality,
        this.getLabel('LBL_TVsSoundQuality')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsEquipmentType: [
        this._model?.assetModelRelatedFields?.networkEquipmentsEquipmentType,
        this.getLabel('LBL_NetworkEquipmentsEquipmentType')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsAreaNetworkType: [
        this._model?.assetModelRelatedFields?.networkEquipmentsAreaNetworkType,
        this.getLabel('LBL_NetworkEquipmentsAreaNetworkType')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsFormFactor: [
        this._model?.assetModelRelatedFields?.networkEquipmentsFormFactor,
        this.getLabel('LBL_NetworkEquipmentsFormFactor')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsNetworkMedia: [
        this._model?.assetModelRelatedFields?.networkEquipmentsNetworkMedia,
        this.getLabel('LBL_NetworkEquipmentsNetworkMedia')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsNumberOfPorts: [
        this._model?.assetModelRelatedFields?.networkEquipmentsNumberOfPorts,
        this.getLabel('LBL_NetworkEquipmentsNumberOfPorts')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsDataRate: [
        this._model?.assetModelRelatedFields?.networkEquipmentsDataRate,
        this.getLabel('LBL_NetworkEquipmentsDataRate')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsPacketForwardingRate: [
        this._model?.assetModelRelatedFields
          ?.networkEquipmentsPacketForwardingRate,
        this.getLabel('LBL_NetworkEquipmentsPacketForwardingRate')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsBoard: [
        this._model?.assetModelRelatedFields?.networkEquipmentsBoard,
        this.getLabel('LBL_NetworkEquipmentsBoard')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsChassisMount: [
        this._model?.assetModelRelatedFields?.networkEquipmentsChassisMount,
        this.getLabel('LBL_NetworkEquipmentsChassisMount')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsbandwidthExpansion: [
        this._model?.assetModelRelatedFields
          ?.networkEquipmentsbandwidthExpansion,
        this.getLabel('LBL_NetworkEquipmentsbandwidthExpansion')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsVoIPBYOD: [
        this._model?.assetModelRelatedFields?.networkEquipmentsVoIPBYOD,
        this.getLabel('LBL_NetworkEquipmentsVoIPBYOD')?.isRequired
          ? Validators.required
          : null,
      ],
      rNetworkEquipmentsNetworkSecurity: [
        this._model?.assetModelRelatedFields?.networkEquipmentsNetworkSecurity,
        this.getLabel('LBL_NetworkEquipmentsNetworkSecurity')?.isRequired
          ? Validators.required
          : null,
      ],
      rCablesPartNumber: [
        this._model?.assetModelRelatedFields?.cablesPartNumber,
        this.getLabel('LBL_CablesPartNumber')?.isRequired
          ? Validators.required
          : null,
      ],
      rCablesConductorSize: [
        this._model?.assetModelRelatedFields?.cablesConductorSize,
        this.getLabel('LBL_CablesConductorSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rCablesConductorDiameter: [
        this._model?.assetModelRelatedFields?.cablesConductorDiameter,
        this.getLabel('LBL_CablesConductorDiameter')?.isRequired
          ? Validators.required
          : null,
      ],
      rCablesInsulationDiameter: [
        this._model?.assetModelRelatedFields?.cablesInsulationDiameter,
        this.getLabel('LBL_CablesInsulationDiameter')?.isRequired
          ? Validators.required
          : null,
      ],
      rCablesThickness: [
        this._model?.assetModelRelatedFields?.cablesThickness,
        this.getLabel('LBL_CablesThickness')?.isRequired
          ? Validators.required
          : null,
      ],
      rCablesOverallDiameter: [
        this._model?.assetModelRelatedFields?.cablesOverallDiameter,
        this.getLabel('LBL_CablesOverallDiameter')?.isRequired
          ? Validators.required
          : null,
      ],
      rCablesNetWeight: [
        this._model?.assetModelRelatedFields?.cablesNetWeight,
        this.getLabel('LBL_CablesNetWeight')?.isRequired
          ? Validators.required
          : null,
      ],
      rRoutersModemsSwitchesRouterWithOrWithoutInternet: [
        this._model?.assetModelRelatedFields
          ?.routersModemsSwitchesRouterWithOrWithoutInternet,
        this.getLabel('LBL_RoutersModemsSwitchesRouterWithOrWithoutInternet')
          ?.isRequired
          ? Validators.required
          : null,
      ],
      rRoutersModemsSwitchesWifiStandard: [
        this._model?.assetModelRelatedFields?.routersModemsSwitchesWifiStandard,
        this.getLabel('LBL_RoutersModemsSwitchesWifiStandard')?.isRequired
          ? Validators.required
          : null,
      ],
      rRoutersModemsSwitchesWirelessFrequency: [
        this._model?.assetModelRelatedFields
          ?.routersModemsSwitchesWirelessFrequency,
        this.getLabel('LBL_RoutersModemsSwitchesWirelessFrequency')?.isRequired
          ? Validators.required
          : null,
      ],
      rRoutersModemsSwitchesSpeedOfTheRouter: [
        this._model?.assetModelRelatedFields
          ?.routersModemsSwitchesSpeedOfTheRouter,
        this.getLabel('LBL_RoutersModemsSwitchesSpeedOfTheRouter')?.isRequired
          ? Validators.required
          : null,
      ],
      rRoutersModemsSwitchesRouterAntennaRange: [
        this._model?.assetModelRelatedFields
          ?.routersModemsSwitchesRouterAntennaRange,
        this.getLabel('LBL_RoutersModemsSwitchesRouterAntennaRange')?.isRequired
          ? Validators.required
          : null,
      ],
      rRecorderOutputLevel: [
        this._model?.assetModelRelatedFields?.recorderOutputLevel,
        this.getLabel('LBL_RecorderOutputLevel')?.isRequired
          ? Validators.required
          : null,
      ],
      rRecorderFrequencyResponse: [
        this._model?.assetModelRelatedFields?.recorderFrequencyResponse,
        this.getLabel('LBL_RecorderFrequencyResponse')?.isRequired
          ? Validators.required
          : null,
      ],
      rRecorderOutputImpedance: [
        this._model?.assetModelRelatedFields?.recorderOutputImpedance,
        this.getLabel('LBL_RecorderOutputImpedance')?.isRequired
          ? Validators.required
          : null,
      ],
      rRecorderDirectivity: [
        this._model?.assetModelRelatedFields?.recorderDirectivity,
        this.getLabel('LBL_RecorderDirectivity')?.isRequired
          ? Validators.required
          : null,
      ],
      rExternalHardDisksCapacities: [
        this._model?.assetModelRelatedFields?.externalHardDisksCapacities,
        this.getLabel('LBL_ExternalHardDisksCapacities')?.isRequired
          ? Validators.required
          : null,
      ],
      rExternalHardDisksInterface: [
        this._model?.assetModelRelatedFields?.externalHardDisksInterface,
        this.getLabel('LBL_ExternalHardDisksInterface')?.isRequired
          ? Validators.required
          : null,
      ],
      rExternalHardDisksColours: [
        this._model?.assetModelRelatedFields?.externalHardDisksColours,
        this.getLabel('LBL_ExternalHardDisksColours')?.isRequired
          ? Validators.required
          : null,
      ],
      rExternalHardDisksSpeed: [
        this._model?.assetModelRelatedFields?.externalHardDisksSpeed,
        this.getLabel('LBL_ExternalHardDisksSpeed')?.isRequired
          ? Validators.required
          : null,
      ],
      rHeadphonesDesign: [
        this._model?.assetModelRelatedFields?.headphonesDesign,
        this.getLabel('LBL_HeadphonesDesign')?.isRequired
          ? Validators.required
          : null,
      ],
      rHeadphonesComfort: [
        this._model?.assetModelRelatedFields?.headphonesComfort,
        this.getLabel('LBL_HeadphonesComfort')?.isRequired
          ? Validators.required
          : null,
      ],
      rHeadphonesSoundQuality: [
        this._model?.assetModelRelatedFields?.headphonesSoundQuality,
        this.getLabel('LBL_HeadphonesSoundQuality')?.isRequired
          ? Validators.required
          : null,
      ],
      rStaplingMachinesWeight: [
        this._model?.assetModelRelatedFields?.staplingMachinesWeight,
        this.getLabel('LBL_StaplingMachinesWeight')?.isRequired
          ? Validators.required
          : null,
      ],
      rStaplingMachinesCapability: [
        this._model?.assetModelRelatedFields?.staplingMachinesCapability,
        this.getLabel('LBL_StaplingMachinesCapability')?.isRequired
          ? Validators.required
          : null,
      ],
      rStaplingMachinesType: [
        this._model?.assetModelRelatedFields?.staplingMachinesType,
        this.getLabel('LBL_StaplingMachinesType')?.isRequired
          ? Validators.required
          : null,
      ],
      rHandToolsOrientation: [
        this._model?.assetModelRelatedFields?.handToolsOrientation,
        this.getLabel('LBL_HandToolsOrientation')?.isRequired
          ? Validators.required
          : null,
      ],
      rHandToolsSize: [
        this._model?.assetModelRelatedFields?.handToolsSize,
        this.getLabel('LBL_HandToolsSize')?.isRequired
          ? Validators.required
          : null,
      ],
      rHandToolsSpan: [
        this._model?.assetModelRelatedFields?.handToolsSpan,
        this.getLabel('LBL_HandToolsSpan')?.isRequired
          ? Validators.required
          : null,
      ],
      rHandToolsMaterial: [
        this._model?.assetModelRelatedFields?.handToolsMaterial,
        this.getLabel('LBL_HandToolsMaterial')?.isRequired
          ? Validators.required
          : null,
      ],
      rHandToolsActivationMethods: [
        this._model?.assetModelRelatedFields?.handToolsActivationMethods,
        this.getLabel('LBL_HandToolsActivationMethods')?.isRequired
          ? Validators.required
          : null,
      ],
      purchaseOrderNumber: [
        this._model?.purchaseOrderNumber,
        this.getLabel('LBL_PurchaseOrderNumber')?.isRequired
          ? Validators.required
          : null,
      ],
      purchaseSupplierCode: [
        this._model?.purchasedFrom,
        this.getLabel('LBL_PurchasedFrom')?.isRequired
          ? Validators.required
          : null,
      ],
      purchaseDate: [
        parseDate(this._model?.purchaseDate),
        this.getLabel('LBL_PurchaseDate')?.isRequired
          ? Validators.required
          : null,
      ],
      purchaseOrderDate: [
        parseDate(this._model?.purchaseOrderDate),
        this.getLabel('LBL_PurchaseOrderDate')?.isRequired
          ? Validators.required
          : null,
      ],
      yearsToDepreciate: [
        this._model?.yearsToDepreciate,
        this.getLabel('LBL_YearsToDepreciate')?.isRequired
          ? Validators.required
          : null,
      ],
      purchaseCost: [
        this._model?.cost,
        this.getLabel('LBL_PurchaseCost')?.isRequired
          ? Validators.required
          : null,
      ],
      scrapValue: [
        this._model?.scrapValue,
        this.getLabel('LBL_ScrapValue')?.isRequired
          ? Validators.required
          : null,
      ],
      depreciationTaken: [
        {
          value:
            this._model?.depreciationTaken !== undefined &&
            this._model?.depreciationTaken !== null
              ? this._model?.depreciationTaken
              : this._model?.id > 0
              ? 0
              : null,
          disabled: true,
        },
      ],
      depreciationTakenDate: [
        {
          value: this.parseLocalDate(
            this._model?.depreciationTakenDate ??
              this._model?.depreciationDate,
          ),
          disabled: true,
        },
      ],
      depreciationDate: [
        {
          value: this.parseLocalDate(
            this._model?.depreciationDate ??
              this._model?.depreciationTakenDate,
          ),
          disabled: true,
        },
      ],
      bookValue: [
        {
          value:
            this._model?.bookValue !== undefined &&
            this._model?.bookValue !== null
              ? this._model?.bookValue
              : this._model?.cost !== undefined &&
                this._model?.cost !== null
              ? Math.max(
                  0,
                  (this._model?.cost ?? 0) -
                    (this._model?.depreciationTaken ?? 0),
                )
              : null,
          disabled: true,
        },
      ],
      fundingSourceCode: [
        this._model?.fundingSource,
        this.getLabel('LBL_FundingSource')?.isRequired
          ? Validators.required
          : null,
      ],
      leaseSupplierCode: [
        this._model?.leasedFrom,
        this.getLabel('LBL_LeasedFrom')?.isRequired
          ? Validators.required
          : null,
      ],
      leaseNumber: [
        this._model?.leaseNumber,
        this.getLabel('LBL_LeaseNumber')?.isRequired
          ? Validators.required
          : null,
      ],
      leaseStartDate: [
        parseDate(this._model?.leaseStartDate),
        this.getLabel('LBL_LeaseStartDate')?.isRequired
          ? Validators.required
          : null,
      ],
      leaseEndDate: [
        parseDate(this._model?.leaseEndDate),
        this.getLabel('LBL_LeaseEndDate')?.isRequired
          ? Validators.required
          : null,
      ],
      depreciationMethod: [
        this._model?.depreciationMethod,
        this.getLabel('LBL_DepreciationMethod')?.isRequired
          ? Validators.required
          : null,
      ],
    });
  }
  // #endregion

  // #region Page Spesific

  _loadSitesDD() {
    this._loadSites(this.sitesService);
  }

  _onCloseSite(data: any) {
    let newItem = data.retSite;
    this.sitesData.push(newItem);
    this._sitesDD.setValue(newItem);
    this._form.patchValue({
      site: newItem,
    });
  }
  // #region Sites Select Popup
  _openSitesList(): void {
    if (
      this._sitesGridFields == undefined ||
      this._sitesGridFields.length == 0
    ) {
      this._getSitesGridFields();
    }

    this._selectSitesGridActive = true;
  }

  _closeSitesList(): void {
    this._selectSitesGridActive = false;
  }

  _returnSelectedSitesHandler(e: SiteLisitng): void {
    this.sitesService
      .getSiteById(this._selectedsite)

      .subscribe((data: any) => {
        if (this.sitesData.find((f) => f.id == data.id) == undefined) {
          this.sitesData.push(data);
        }

        this._sitesDD.setValue(data);
        this._form.patchValue({ site: data });
      });

    this._closeSitesList();
  }

  _getSitesGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._sitesPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._sitesPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._sitesPageName,
      (gridColumns) => {
        this._sitesGridFields = gridColumns;
      },
    );
  }

  _getSites = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    return this.sitesService.getSitesGrid(params, model);
  };

  _onSiteRowSelectionChanges(e: any[]) {
    this._selectedsite = e[0];
  }

  _onSiteObjectRowSelectionChanges(e: any[]) { }

  onSitesChange(e: SiteLisitng) {
    setTimeout(() => {
      this.getAssetModelsDD();
      this.getPersonsDD();
      this.getVendorsDD();
      this.getLocationsDD();
    }, 100);
  }
  // #endregion
  //#region Transactions Field Editor
  onShowTransactionFieldEditor(objectCode: string) {
    this._showTransactionFieldEditor = true;
    this._transactionsFieldEditorObjectCode = objectCode;
  }

  onCloseTransactionFieldEditor() {
    this._showTransactionFieldEditor = false;
  }
  //#endregion

  setModelTypeId(val: IAssetModelDto): void {
    if (this._modelTypeId != val.assetTypeId) {
      this._modelTypeId = val.assetTypeId;
      this._modelTypeFilesPics = [];
      this._modelTypeFilesDoc = [];
      this._assetModelService
        .getAssetModelById(val.id)

        .subscribe((model) => {
          this._modelTypeInfo = model;
          this._modelsDD.setValue(model);
          this._form.patchValue({
            modelNumber: model,
          });

          if (this._modelTypeInfo?.modelFiles) {
            this._modelTypeInfo.modelFiles.forEach((element: any) => {
              if (element.file?.type.includes('image')) {
                this._modelTypeFilesPics.push(element);
              } else if (element.file?.type.includes('application')) {
                this._modelTypeFilesDoc.push(element);
              }
            });
          }
        });
    }
  }

  _onModelChange(modelId: number): void {
    if (this._modelTypeId != modelId) {
      this._modelTypeId = modelId;
      this._modelTypeFilesPics = [];
      this._modelTypeFilesDoc = [];
      this._assetModelService
        .getAssetModelById(this._modelTypeId)

        .subscribe((model) => {
          this._modelTypeInfo = model;
          this._modelsDD.setValue(model);
          this._form.patchValue({
            modelNumber: model,
          });

          if (this._modelTypeInfo?.modelFiles) {
            this._modelTypeInfo.modelFiles.forEach((element: any) => {
              if (element.file?.type.includes('image')) {
                this._modelTypeFilesPics.push(element);
              } else if (element.file?.type.includes('application')) {
                this._modelTypeFilesDoc.push(element);
              }
            });
          }
        });
    }
  }
  // #region Models Drop Down
  getAssetModelsDD(): void {
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }

    let model = {};

    if (this._currentUser.isEnterprise) {
      model = { accountIds: this._selectedAccounts.map((x) => x.accountId) };
    }
    this._assetModelService
      .getAssetModelListing(null, siteIdd, model)

      .subscribe((data: any) => {

        this.modelsData = data.body.data;
      });
  }

  _openModelList(): void {
    if (
      this._modelGridFields == undefined ||
      this._modelGridFields.length == 0
    ) {
      this._getModelGridFields();
    }
    this._modelListEnabled = true;
  }
  _getModelGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._modelPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._modelPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._modelPageName,
      (gridColumns) => {
        this._modelGridFields = gridColumns;
      },
    );
  }
  _closeModelList(): void {
    this._modelListEnabled = false;
  }
  _returnSelectedModelHandler(e: IAssetModelDto): void {
    this._assetModelService
      .getAssetModelById(this._selectedModel)
      .subscribe((data: any) => {
        if (this.modelsData.find((f) => f.id == data.id) == undefined) {
          this.modelsData.push(data);
        }
        this._modelsDD.setValue(data);
        this._form.patchValue({
          modelNumber: data,
        });
      });
    this._closeModelList();
  }
  _onModelRowSelectionChanges(e: any[]) {
    this._selectedModel = e[0];
  }
  _onModelObjectRowSelectionChanges(e: any[]) { }
  _getModels = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }
    if (this._currentUser.isEnterprise) {
      model.accountIds = this._selectedAccounts.map((x) => x.accountId);
    }
    return this._assetModelService.getAssetModelGrid(params, model, siteIdd);
  };
  _onCloseAddAssetModel(data: any) {
    let newItem = data.response;
    this.modelsData.push(newItem);
    this._modelsDD.setValue(newItem);
    this._form.patchValue({
      modelNumber: newItem,
    });
  }
  // #endregion

  // #region Acquisition Type Drop Down
  getAcquisitionTypesDD(): void {
    this._acquisitionTypeService
      .getAcquisitionTypeListing()
      .subscribe((data: any) => {
        this.acquisitionTypesData = [...data];
        if (
          this._pageState == PageState.ADD &&
          (this._model?.id == 0 || this._model?.id == undefined)
        ) {
          this._form.patchValue({
            acquisitionType: data[0],
          });
        }
      });
  }
  setAcquisitionTypeId(val: IAcquisitionTypeDto): void {
    this._acquisitionTypeId = val.id;
  }
  // #endregion

  // #region Asset Assignees
  _selectAssigneesHandler(e: any, _inputAssigneesType: AssigneesType) {
    this._assigneeType = _inputAssigneesType;
    if (
      this._assetsGridFields == undefined ||
      this._assetsGridFields.length == 0
    ) {
      this._getAssetsGridFields();
    }
    this.assetAssigneesGridExecludedFields = [
      'assetFiles.file.thumbnail',
      'model.modelFiles.file.thumbnail',
    ];
    this._showAssigneesGrid = true;
  }
  _closeAssetsList() {
    this._showAssigneesGrid = false;
  }
  _getAssetsGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._page)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(this._page, data);
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._page,
      (gridColumns) => {
        this._assetsGridFields = gridColumns;
      },
    );
  }
  _removeAssigneeHandler(item: IAssetAssignees, _assigneesType: AssigneesType) {
    const index = this._assetAssignees.indexOf(item, 0);
    if (index > -1) {
      this._assetAssignees.splice(index, 1);
      this._selectedAssigneesKeys.splice(this._assetAssignees.indexOf(item), 1);
    }
    if (_assigneesType == AssigneesType.general) {
      let indx = this._assetAssignees_General.indexOf(item, 0);
      if (indx > -1) {
        this._assetAssignees_General.splice(index, 1);
      }
    } else if (_assigneesType == AssigneesType.software) {
      let indx = this._assetAssignees_Software.indexOf(item, 0);
      if (indx > -1) {
        this._assetAssignees_Software.splice(index, 1);
      }
    } else if (_assigneesType == AssigneesType.computer) {
      let indx = this._assetAssignees_Computer.indexOf(item, 0);
      if (indx > -1) {
        this._assetAssignees_Computer.splice(index, 1);
      }
    }
  }
  _getAssets = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    let currentAssetId = this._model?.id ?? 0;
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }
    if (this._assigneeType == AssigneesType.general) {
      return this._assetService.getAssetGrid(
        params,
        model,
        null,
        currentAssetId,
        1,
        null,
        null,
        siteIdd,
      );
    }
    if (this._assigneeType == AssigneesType.computer) {
      return this._assetService.getAssetGrid(
        params,
        model,
        3,
        currentAssetId,
        0,
        null,
        null,
        siteIdd,
      );
    }
    if (this._assigneeType == AssigneesType.software) {
      return this._assetService.getAssetGrid(
        params,
        model,
        9,
        currentAssetId,
        0,
        null,
        null,
        siteIdd,
      );
    }
  };
  _returnSelectedAssetHandler(item: IAssetDto) {
    if (this._currentUser.isEnterprise) {
      if (this._selectedAccountIds.length) {
        this._showAssigneesGrid = false;
        this._selectedAssigneesKeys = this._selectedAssignees;
        this._addAssignee();
      } else {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please choose the account with the site desired',
          withoutConfirm: true,
        });
      }
    } else if (!this._currentUser.isEnterprise) {
      this._showAssigneesGrid = false;
      this._selectedAssigneesKeys = this._selectedAssignees;
      this._addAssignee();
    }
  }
  _addAssignee(item: IAssetDto = null) {
    if (item == null) {
      this._assetAssignees =
        this._assetAssignees === null ? [] : this._assetAssignees;

      if (this._selectedObjectAssets.length > 0) {
        this._selectedObjectAssets.forEach((element: any) => {
          let addedAssginee: IAssetAssignees = <IAssetAssignees>{
            assetNumber: element?.assetNumber ?? '',
            assignedAssetId: element.id,
            assignedAsset: element,
            asset: this._model,
            assetId: this._model?.id ? this._model?.id : 0,
            assigneesType: this._assigneeType,
            status: EntityStatusOptions.Active,
          };
          if (this._pageState == PageState.EDIT) {
            let sameAsParent = this._model?.id == element.id;
            if (sameAsParent) {
              return;
            }
          }
          let exists = this._assetAssignees.find(
            (f) =>
              f.assignedAssetId == element.id &&
              f.status == EntityStatusOptions.Active,
          );
          if (exists == undefined) {
            this._assetAssignees.push(addedAssginee);
          }
          if (this._assigneeType == AssigneesType.general) {
            let exists_general = this._assetAssignees_General.find(
              (f) =>
                f.assignedAssetId == element.id &&
                f.status == EntityStatusOptions.Active,
            );
            if (exists_general == undefined) {
              this._assetAssignees_General.push(addedAssginee);
            }
          } else if (this._assigneeType == AssigneesType.software) {
            let exists_software = this._assetAssignees_Software.find(
              (f) =>
                f.assignedAssetId == element.id &&
                f.status == EntityStatusOptions.Active,
            );
            if (exists_software == undefined) {
              this._assetAssignees_Software.push(addedAssginee);
            }
          } else if (this._assigneeType == AssigneesType.computer) {
            let exists_computer = this._assetAssignees_Computer.find(
              (f) =>
                f.assignedAssetId == element.id &&
                f.status == EntityStatusOptions.Active,
            );
            if (exists_computer == undefined) {
              this._assetAssignees_Computer.push(addedAssginee);
            }
          }
        });
      }
    }
  }
  _onAssetRowSelectionChanges(e: any[]) {
    this._selectedAssignees = [];
    e.forEach((item) => {
      this._selectedAssignees.push(+item);
    });
  }
  _onAssetObjectRowSelectionChanges(e: any[]) {
    this._selectedObjectAssets = [];
    this._selectedObjectAssets = e;
  }
  displayAssetImage(input: string, ListOfItems: any = null) {
    let primaryPicture;
    if (ListOfItems != null && ListOfItems.length > 0) {
      primaryPicture = ListOfItems.find(
        (v: any) => v.assetFileisPrimary == true,
      );
      if (primaryPicture == undefined && primaryPicture == null) {
        primaryPicture = ListOfItems[0];
      }
      input = primaryPicture.file.thumbnail;
      return (
        EnvironmentLoaderService.envConfig.enterpriseApiLocal + '/' + input
      );
    } else return '';
  }
  displayModelImage(input: string, ListOfItems: any = null) {
    let primaryPicture;
    if (ListOfItems != null && ListOfItems.length > 0) {
      primaryPicture = ListOfItems.find(
        (v: any) => v.modelFileisPrimary == true,
      );
      if (primaryPicture == undefined && primaryPicture == null) {
        primaryPicture = ListOfItems[0];
      }
      input = primaryPicture.file.thumbnail;
      return (
        EnvironmentLoaderService.envConfig.enterpriseApiLocal + '/' + input
      );
    } else return '';
  }
  _closeNotesWindow() {
    this._showFileNotes = false;
  }
  _showNotesWindow(id: number) {
    this._assetService
      .getAssetById(id)

      .subscribe((model: any) => {
        this._notesModel = model;
        this._showFileNotes = true;
      });
  }
  // #endregion

  // #region Depreciation Method Drop Down
  getDepreciationMethodsDD(): void {
    this._depreciationMethodService
      .getDepreciationMethodListing()
      .subscribe((data: any) => {
        this.depreciationMethodsData = [...data];
        if (
          this._pageState == PageState.ADD &&
          (this._model?.id == 0 || this._model?.id == undefined)
        ) {
          this._form.patchValue({
            depreciationMethod: data[0],
          });
        }
      });
  }
  /* setDepreciationMethodId(val: IAcquisitionTypeDto):void {
    this._depreciationMethodId = val.id;
  } */
  // #endregion

  // #region Conditions Drop Down
  getConditionsDD(): void {
    let model = {};
    if (this._currentUser.isEnterprise) {
      model = { accountIds: this._selectedAccounts.map((x) => x.accountId) };
    }

    this._conditionsService
      .getConditionListing(model)
      .subscribe((data: any) => {
        this.conditionsData = data;
      });
  }
  _openConditionsList(): void {
    if (
      this._conditionsGridFields == undefined ||
      this._conditionsGridFields.length == 0
    ) {
      this._getConditionsGridFields();
    }
    this._conditionsListEnabled = true;
  }
  _getConditionsGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._conditionsPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._conditionsPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._conditionsPageName,
      (gridColumns) => {
        this._conditionsGridFields = gridColumns;
      },
    );
  }
  _closeConditionsList(): void {
    this._conditionsListEnabled = false;
  }
  _returnSelectedConditionsHandler(e: ConditionDto): void {
    this._conditionsService
      .getConditionById(this._selectedCondition)
      .subscribe((data: any) => {
        if (this.conditionsData.find((f) => f.id == data.id) == undefined) {
          this.conditionsData.push(data);
        }
        this._conditionsDD.setValue(data);
        this._form.patchValue({
          conditionCode: data,
        });
      });
    this._closeConditionsList();
  }
  _onConditionsRowSelectionChanges(e: any[]) {
    this._selectedCondition = e[0];
  }
  _onConditionsObjectRowSelectionChanges(e: any[]) { }
  _getConditions = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    if (this._currentUser.isEnterprise) {
      //model.accountIds = this._selectedAccountIds;
      model.accountIds = this._selectedAccounts.map((x) => x.accountId);
    }
    return this._conditionsService.getConditionGrid(params, model);
  };
  _onCloseAddCondition(data: any) {
    let newItem = data.response;
    this.conditionsData.push(newItem);
    this._conditionsDD.setValue(newItem);
    this._form.patchValue({
      conditionCode: newItem,
    });
  }
  // #endregion

  // #region Statuses Drop Down
  getStatusesDD(): void {
    let model = {};
    if (this._currentUser.isEnterprise) {
      model = { accountIds: this._selectedAccounts.map((x) => x.accountId) };
    }
    this._statusService.getStatusListing(model).subscribe((data: any) => {
      this.statusesData = data;
    });
  }
  _openStatusesList(): void {
    if (
      this._statusesGridFields == undefined ||
      this._statusesGridFields.length == 0
    ) {
      this._getStatusesGridFields();
    }
    this._statusesListEnabled = true;
  }
  _getStatusesGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._statusesPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._statusesPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._statusesPageName,
      (gridColumns) => {
        this._statusesGridFields = gridColumns;
      },
    );
  }
  _closeStatusesList(): void {
    this._statusesListEnabled = false;
  }
  _returnSelectedStatusesHandler(e: StatusDto): void {
    this._statusService
      .getStatusById(this._selectedStatus)
      .subscribe((data: any) => {
        if (this.statusesData.find((f) => f.id == data.id) == undefined) {
          this.statusesData.push(data);
        }
        this._statusesDD.setValue(data);
        this._form.patchValue({
          assetStatusesCode: data,
        });
      });
    this._closeStatusesList();
  }
  _onStatusesRowSelectionChanges(e: any[]) {
    this._selectedStatus = e[0];
  }
  _onStatusesObjectRowSelectionChanges(e: any[]) { }
  _getStatuses = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    if (this._currentUser.isEnterprise) {
      model.accountIds = this._selectedAccounts.map((x) => x.accountId);
    }
    return this._statusService.getStatusGrid(params, model);
  };
  _onCloseAddStatuses(data: any) {
    let newItem = data.response;
    this.statusesData.push(newItem);
    this._statusesDD.setValue(newItem);
    this._form.patchValue({
      assetStatusesCode: data,
    });
  }
  // #endregion

  // #region Persons Drop Down
  getPersonsDD(): void {
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }
    let model = {};
    if (this._currentUser.isEnterprise) {
      model = { accountIds: this._selectedAccounts.map((x) => x.accountId) };
    }
    this._usersService
      .getActivePersons(null, siteIdd, model)

      .subscribe((data: any) => {
        this.personsData = data.body;
        const personId = this._model?.personId || this._model?.person?.id;
        if (personId) {
          const matchingPerson = this.personsData?.find((f) => f.id === personId);
          if (matchingPerson) {
            this._personsDD.setValue(matchingPerson);
            this._form.patchValue({
              personCode: matchingPerson,
            });
          }
        }
      });
  }
  _openPersonsList(): void {
    if (
      this._personsGridFields == undefined ||
      this._personsGridFields.length == 0
    ) {
      this._getPersonsGridFields();
    }
    this._personsListEnabled = true;
  }
  _getPersonsGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._personsPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._personsPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._personsPageName,
      (gridColumns) => {
        this._personsGridFields = gridColumns;
      },
    );
  }
  _closePersonsList(): void {
    this._personsListEnabled = false;
  }
  _returnSelectedPersonsHandler(e: UserSummary): void {
    this._usersService.getUser(this._selectedPerson).subscribe((data: any) => {
      if (this.personsData.find((f) => f.id == data.id) == undefined) {
        this.personsData.push(data);
      }
      this._personsDD.setValue(data);
      this._form.patchValue({
        personCode: data,
      });
    });
    this._closePersonsList();
  }
  _onPersonsRowSelectionChanges(e: any[]) {
    this._selectedPerson = e[0];
  }
  _onPersonsObjectRowSelectionChanges(e: any[]) { }
  _getPersons = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }
    if (this._currentUser.isEnterprise) {
      model.accountIds = this._selectedAccounts.map((x) => x.accountId);
    }
    return this._usersService.getPersonsGridData(params, model, null, siteIdd);
  };
  _onCloseAddPersons(data: any) {
    let newItem = data.response;
    this.personsData.push(newItem);
    this._personsDD.setValue(newItem);
    this._form.patchValue({
      personCode: data,
    });
  }
  getUserSites(sites: any[]) {
    var retVal: string = '';
    retVal = sites?.map((d) => d.site?.code ?? 'All Sites').join(', ');
    return retVal;
  }
  GetPrimaryManagerData(Managers: any, field: string) {
    if (Managers && Managers.length > 0) {
      let PrimaryManager: any = Managers.find((x: any) => x.isPrimary == true);
      if (PrimaryManager && PrimaryManager.manager) {
        if (field == 'managers.manager.code')
          return PrimaryManager.manager.code;
        if (field == 'managers.manager.firstName')
          return PrimaryManager.manager.firstName;
        if (field == 'managers.manager.lastName')
          return PrimaryManager.manager.lastName;
      }
    }

    return '';
  }
  // #endregion

  // #region FundingSource Drop Down
  getFundingsourcesDD(): void {
    let model = {};
    if (
      this._currentUser.isEnterprise &&
      this._pageState !== PageState.EDIT &&
      this._pageState !== PageState.VIEW
    ) {
      model = { accountIds: this._selectedAccounts.map((x) => x.accountId) };
    }
    this._fundingSourceService
      .getFundingSourceListing(model)
      .subscribe((data: any) => {
        this.fundingSourcesData = data;
        const fundingSourceId =
          this._model?.fundingSourceId ||
          this._model?.fundingSource?.id ||
          this._form?.controls?.fundingSourceCode?.value?.id;
        if (fundingSourceId) {
          const matchingFundingSource = this.fundingSourcesData?.find(
            (f) => f.id === fundingSourceId,
          );
          if (matchingFundingSource) {
            this._fundingSourcesDD.setValue(matchingFundingSource);
            this._form.patchValue({
              fundingSourceCode: matchingFundingSource,
            });
          } else {
            this._fundingSourceService
              .getFundingSourceById(fundingSourceId)
              .subscribe((fundingSourceData: any) => {
                if (fundingSourceData) {
                  if (!this.fundingSourcesData) {
                    this.fundingSourcesData = [];
                  }
                  if (
                    !this.fundingSourcesData.find(
                      (f) => f.id === fundingSourceData.id,
                    )
                  ) {
                    this.fundingSourcesData.push(fundingSourceData);
                  }
                  this._fundingSourcesDD.setValue(fundingSourceData);
                  this._form.patchValue({
                    fundingSourceCode: fundingSourceData,
                  });
                }
              });
          }
        }
      });
  }
  _openFundingSourcesList(): void {
    if (
      this._fundingSourcesGridFields == undefined ||
      this._fundingSourcesGridFields.length == 0
    ) {
      this._getFundingSourcesGridFields();
    }
    this._fundingSourcesListEnabled = true;
  }
  _getFundingSourcesGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._fundingSourcesPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._fundingSourcesPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._fundingSourcesPageName,
      (gridColumns) => {
        this._fundingSourcesGridFields = gridColumns;
      },
    );
  }
  _closeFundingSourcesList(): void {
    this._fundingSourcesListEnabled = false;
  }
  _returnSelectedFundingSourcesHandler(e: UserSummary): void {
    this._fundingSourceService
      .getFundingSourceById(this._selectedFundingSource)
      .subscribe((data: any) => {
        if (this.fundingSourcesData.find((f) => f.id == data.id) == undefined) {
          this.fundingSourcesData.push(data);
        }
        this._fundingSourcesDD.setValue(data);
        this._form.patchValue({
          fundingSourceCode: data,
        });
      });
    this._closeFundingSourcesList();
  }
  _onFundingSourcesRowSelectionChanges(e: any[]) {
    this._selectedFundingSource = e[0];
  }
  _onFundingSourcesObjectRowSelectionChanges(e: any[]) { }
  _getFundingSources = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    if (this._currentUser.isEnterprise) {
      model.accountIds = this._selectedAccounts.map((x) => x.accountId);
    }
    return this._fundingSourceService.getFundingSourceGrid(params, model);
  };
  _onCloseAddFundingSources(data: any) {
    let newItem = data.response;
    this.fundingSourcesData.push(newItem);
    this._fundingSourcesDD.setValue(newItem);
    this._form.patchValue({
      fundingSourceCode: data,
    });
  }
  // #endregion

  // #region Location / home Location Drop Down
  // Common
  getLocationsDD(): void {
    if (this._currentUser.isEnterprise) {
      let model = { siteIds: this._selectedAccounts.map((x) => x.siteId) };
      //this._locationsService.getLocationsByAccounts(model)
      this._locationsService
        .getLocationsBySitesEnterprise(model)

        .subscribe((data: any) => {
          this.locationsData = data;
          const locationId = this._model?.locationId || this._model?.location?.id;
          if (locationId) {
            const matchingLocation = this.locationsData?.find(
              (f) => f.id === locationId,
            );
            if (matchingLocation) {
              this._locationsDD.setValue(matchingLocation);
              this._form.patchValue({
                locationCode: matchingLocation,
              });
            }
          }
        });
    }

    if (!this._currentUser.isEnterprise) {
      let siteIdd: number | null;
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
      let siteModel = {
        siteId: siteIdd,
      };
      this._locationsService
        .getLocationsByAccounts(siteModel)

        .subscribe((data: any) => {
          this.locationsData = data;
          const locationId = this._model?.locationId || this._model?.location?.id;
          if (locationId) {
            const matchingLocation = this.locationsData?.find(
              (f) => f.id === locationId,
            );
            if (matchingLocation) {
              this._locationsDD.setValue(matchingLocation);
              this._form.patchValue({
                locationCode: matchingLocation,
              });
            }
          }
        });
    }
  }
  _openLocationsList(_currentLocation: CurrentSelectedAssetLocation): void {
    this._activeLocationItem = _currentLocation;
    if (
      this._locationsGridFields == undefined ||
      this._locationsGridFields.length == 0
    ) {
      this._getLocationsGridFields();
    }
    this._locationsListEnabled = true;
  }
  _getLocationsGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._locationsPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._locationsPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._locationsPageName,
      (gridColumns) => {
        this._locationsGridFields = gridColumns;
      },
    );
  }
  _closeLocationsList(): void {
    this._locationsListEnabled = false;
  }
  _returnSelectedLocationsHandler(e: LocationSummary): void {
    this._locationsService
      .getLocation(this._selectedLocation)
      .subscribe((data: any) => {
        if (this.locationsData.find((f) => f.id == data.id) == undefined) {
          this.locationsData.push(data);
        }
        if (
          this._activeLocationItem == CurrentSelectedAssetLocation.homeLocation
        ) {
          this._homeLocationsDD.setValue(data);
          this._form.patchValue({
            homeLocationCode: data,
          });
        } else if (
          this._activeLocationItem == CurrentSelectedAssetLocation.location
        ) {
          this._locationsDD.setValue(data);
          this._form.patchValue({
            locationCode: data,
          });
        }
      });
    this._closeLocationsList();
  }
  _onLocationsRowSelectionChanges(e: any[]) {
    this._selectedLocation = e[0];
  }
  _onLocationsObjectRowSelectionChanges(e: any[]) { }
  _getLocations = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }
    if (this._currentUser.isEnterprise) {
      //model.accountIds = this._selectedAccountIds;
      model.siteIds = this._selectedAccounts.map((x) => x.siteId);
    }

    return this._locationsService.getLocations(params, model, siteIdd);
  };
  _onOpenLocationForm(_currentLocation: CurrentSelectedAssetLocation): void {
    this._activeLocationItem = _currentLocation;
    this._openForm(this._locationsPageName);
  }
  _onCloseAddLocation(data: any) {
    let newItem = data.response;
    this.locationsData.push(newItem);
    if (this._activeLocationItem == CurrentSelectedAssetLocation.homeLocation) {
      this._homeLocationsDD.setValue(newItem);
      this._form.patchValue({
        homeLocationCode: data,
      });
    } else if (
      this._activeLocationItem == CurrentSelectedAssetLocation.location
    ) {
      this._locationsDD.setValue(newItem);
      this._form.patchValue({
        locationCode: data,
      });
    }
  }
  // #endregion

  // #region Maintenance / Purchase Supplier Drop Down
  // Common
  getVendorsDD(): void {
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }
    let model = {};
    if (this._currentUser.isEnterprise) {
      model = { accountIds: this._selectedAccounts.map((x) => x.accountId) };
    }
    this._vendorsService
      .getVendorsListing(null, siteIdd, model)

      .subscribe((data: any) => {
        this.vendorsData = data;
      });
  }
  _openVendorsList(_currentVendor: CurrentSelectedSupplier): void {
    this._activeVendorItem = _currentVendor;
    if (
      this._vendorsGridFields == undefined ||
      this._vendorsGridFields.length == 0
    ) {
      this._getVendorsGridFields();
    }
    this._supplierListEnabled = true;
  }
  _getVendorsGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._vendorsPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._vendorsPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._vendorsPageName,
      (gridColumns) => {
        this._vendorsGridFields = gridColumns;
      },
    );
  }
  _closeVendorsList(): void {
    this._supplierListEnabled = false;
  }
  _returnSelectedVendorsHandler(e: VendorSummary): void {
    this._vendorsService
      .getVendor(this._selectedSupplier)
      .subscribe((data: any) => {
        if (this.vendorsData.find((f) => f.id == data.id) == undefined) {
          this.vendorsData.push(data);
        }
        if (
          this._activeVendorItem == CurrentSelectedSupplier.maintenanceSupplier
        ) {
          this._maintananceSuppliersDD.setValue(data);
          this._form.patchValue({
            maintananceSupplierCode: data,
          });
        } else if (
          this._activeVendorItem == CurrentSelectedSupplier.purchaseSupplier
        ) {
          this._purchaseSuppliersDD.setValue(data);
          this._form.patchValue({
            purchaseSupplierCode: data,
          });
        } else if (
          this._activeVendorItem == CurrentSelectedSupplier.leaseSupplier
        ) {
          this._leaseSuppliersDD.setValue(data);
          this._form.patchValue({
            leaseSupplierCode: data,
          });
        }
      });
    this._closeVendorsList();
  }
  _onVendorsRowSelectionChanges(e: any[]) {
    this._selectedSupplier = e[0];
  }
  _onVendorsObjectRowSelectionChanges(e: any[]) { }
  _getVendors = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    let siteIdd: number | null;
    if (!this._currentUser.isEnterprise) {
      siteIdd = this._form.get('site').value?.id ?? this.siteId;
    }
    if (this._currentUser.isEnterprise) {
      model.accountIds = this._selectedAccounts.map((x) => x.accountId);
    }
    return this._vendorsService.getVendors(params, model, siteIdd);
  };
  _onOpenVendorForm(_currentVendor: CurrentSelectedSupplier): void {
    this._activeVendorItem = _currentVendor;
    this._openForm(this._vendorsPageName);
  }
  _onCloseAddVendor(data: any) {
    let newItem = data.response;
    this.vendorsData.push(newItem);
    if (this._activeVendorItem == CurrentSelectedSupplier.maintenanceSupplier) {
      this._maintananceSuppliersDD.setValue(newItem);
      this._form.patchValue({
        maintananceSupplierCode: newItem,
      });
    } else if (
      this._activeVendorItem == CurrentSelectedSupplier.purchaseSupplier
    ) {
      this._purchaseSuppliersDD.setValue(newItem);
      this._form.patchValue({
        purchaseSupplierCode: newItem,
      });
    } else if (
      this._activeVendorItem == CurrentSelectedSupplier.leaseSupplier
    ) {
      this._leaseSuppliersDD.setValue(newItem);
      this._form.patchValue({
        leaseSupplierCode: newItem,
      });
    }
  }
  // #endregion

  // #region Date Comparison
  compareLeaseDates() {
    let startDate = this._form.controls.leaseStartDate.value;
    let endDate = this._form.controls.leaseEndDate.value;
    if (startDate && endDate) {
      if (startDate > endDate) {
        this._layoutComponent.openPopup({
          title: 'Alert',
          message: 'The lease start date should be earlier than end date',
          withoutConfirm: true,
        });
        this._form.patchValue({
          leaseStartDate: null,
          leaseEndDate: null,
        });
      }
    }
  }
  compareWarrantyDates() {
    let startDate = this._form.controls.warrantyStartDate.value;
    let endDate = this._form.controls.warrantyEndDate.value;
    if (startDate && endDate) {
      if (startDate > endDate) {
        this._layoutComponent.openPopup({
          title: 'Alert',
          message: 'The warranty start date should be earlier than end date',
          withoutConfirm: true,
        });
        this._form.patchValue({
          warrantyStartDate: null,
          warrantyEndDate: null,
        });
      }
    }
  }
  compareMaintananceContractDates() {
    let startDate = this._form.controls.maintananceContractStartDate.value;
    let endDate = this._form.controls.maintananceContractEndDate.value;
    if (startDate && endDate) {
      if (startDate > endDate) {
        this._layoutComponent.openPopup({
          title: 'Alert',
          message:
            'The maintanance contract start date should be earlier than end date',
          withoutConfirm: true,
        });
        this._form.patchValue({
          maintananceContractStartDate: null,
          maintananceContractEndDate: null,
        });
      }
    }
  }
  // #endregion

  // #endregion

  // #region Fields Tab Containers
  getCustomForm(event: any) {
    this._customForm = event;
  }

  _onTabSelect(e: SelectEvent): void {
    this._mobileMenuToggle();
    if (e?.title === 'Scheduled Maintenance' || e?.title === 'Schedule Maintenance') {
      this.loadScheduledMaintenanceData();
    } else if (e?.title === 'Maintenance History' || e?.title === 'Performed Maintenance History') {
      this.loadPerformedMaintenanceHistoryData();
    }
  }

  _mobileMenuToggle() {
    let mobiletabstrip = this.screenTabs.nativeElement.querySelector('ul');

    if (mobiletabstrip.classList != undefined) {
      mobiletabstrip.classList.toggle('visibletab');
    }
  }

  getFormTabs() {
    try {
      this._gridFields
        .getformTabsByPage(this._page)

        .subscribe((x: FormTabSummary[]) => {
          this._dataTabs = x;
          this._dataTabsSelected = x;
          this._userNewDataTabs = x;
          //this.sortProduct(p => p.order, "ASC");
        });
    } catch { }
  }

  getSystemFieldsAndCustomFields() {
    this._gridFields
      .getSystemFieldsByPage(this._page)

      .subscribe((x: GridFieldSummary[]) => {
        this._systemFields = [...x];

        this._editedSystemFields = [...x];
        /* this._systemFields.forEach((s:GridFieldSummary, indx: number) =>{
        this._tabDisabledIndexes.push(indx);
      }) */
        this.loadCustomFields(this._model?.id?.toString());
      });
  }

  private loadCustomFields(entityId?: string) {
    entityId = this.copyModelId?.toString() ?? this._model?.id?.toString();
    //this._pacer.start();
    this._customFieldsService
      .getCustomFields(this._entity, this._page)

      .subscribe((data) => {
        data.forEach((fieldData: CustomFieldSummary) => {
          if (fieldData.customFieldType.type.toLowerCase() == 'date') {
            if (fieldData.customFieldsData) {
              let _cutomfieldData = fieldData.customFieldsData.find(
                (f) => f.entityId == entityId,
              );
              if (_cutomfieldData && _cutomfieldData.value) {
                _cutomfieldData.value = new Date(
                  _cutomfieldData.value,
                ).toUTCString();
              }
            }
          }
        });

        data = data.filter((x) => x.formTab?.page == this._page);
        if (
          this._model != null &&
          (this._model.id != null || this.copyModelId != null)
        ) {
          let that = this;
          data.map(function (x) {
            x.entityId = that._model.id ?? that.copyModelId;
          });
        }
        var tabIds = data
          .map((item) => item.formTabId)
          .filter((value, index, self) => self.indexOf(value) === index);

        const grouped = this.groupBy(
          data,
          (item: { formTabId: any }) => item.formTabId,
        );
        let that = this;
        tabIds.forEach(function (formTabId) {
          that._customFields[formTabId] = grouped.get(formTabId);
        });
        if (this.copyModelId) {
          let CustomFieldsDataForCopy: any = {};
          grouped.forEach((element) => {
            element.forEach((value: any) => {
              let _cutomfieldData = value.customFieldsData.find(
                (f: any) => f.entityId == entityId,
              );
              CustomFieldsDataForCopy[value.name] =
                _cutomfieldData.value ?? null;
              if (
                value.customFieldType.type.toLowerCase() == 'date' &&
                CustomFieldsDataForCopy[value.name]
              ) {
                CustomFieldsDataForCopy[value.name] = new Date(
                  CustomFieldsDataForCopy[value.name],
                );
              }
              if (
                value.customFieldType.type.toLowerCase() == 'numeric' &&
                CustomFieldsDataForCopy[value.name]
              )
                CustomFieldsDataForCopy[value.name] = parseFloat(
                  CustomFieldsDataForCopy[value.name],
                );
            });
            this._customFieldsValueChanges(CustomFieldsDataForCopy, null);
          });
        }
        this.setMandatoryFields();
      });
    //this._pacer.start()
    this._customFieldsService
      .getFormTabsByPage(this._page)

      .subscribe((data: any) => {
        //get custom tabs first
        this._customTabs = data.filter(
          (x: { isCustom: boolean }) => x.isCustom == true,
        );
      });
  }
  groupBy(list: any[], keyGetter: any) {
    const map = new Map();
    list.forEach((item) => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }
  setMandatoryFields() {
    this._requiredFieldNames = [];
    this._requiredFields = [];
    this.requiredSystemTabs = [];
    this._customValidation = [];
    setTimeout(() => {
      if (this._systemFields) {
        for (let item of this._systemFields) {
          if (item?.gridFieldText?.isRequired) {
            this._requiredFields.push(item);
          }
        }
      }

      if (this._customFields) {
        for (let item2 of this._customFields) {
          if (item2) {
            for (let item3 of item2) {
              if (item3?.isRequired) {
                this._requiredFields.push(item3);
              }
            }
          }
        }
      }
      this._requiredFields.forEach(
        (element: { isCustomField: any; formTab: any; formTabId: any }) => {
          if (!element.isCustomField && element.isCustomField == null) {
            var tabName = element.formTab;
            this._requiredFieldNames.push(tabName);
          } else {
            if (element.formTabId) {
              let SystemFieldTab = this._dataTabs.find(
                (x) => x.id == element.formTabId,
              );
              if (SystemFieldTab) this.requiredSystemTabs.push(SystemFieldTab);
            }
          }
        },
      );
      if (this.screenTabs?.nativeElement) {
        let tabElementList = this.screenTabs.nativeElement.querySelector('ul');
        if (tabElementList) {
          Array.from(tabElementList.children).forEach((child: any) => {
            child.classList.remove('mendatoryTab');
          });
          Array.from(tabElementList.children).forEach((child: any) => {
            if (
              this._requiredFieldNames.find(
                (x: { name: any; page: string; formtab: { page: string } }) =>
                  x.name === child.firstChild?.innerText &&
                  (x.page ? x.page == this._page : x.formtab?.page == this._page),
              )
            ) {
              child.classList.add('mendatoryTab');
            }
            if (
              this.requiredSystemTabs.find(
                (x: any) => x.name === child.firstChild?.innerText,
              )
            ) {
              child.classList.add('mendatoryTab');
            }
          });
        }
      }
      this._requiredFields.forEach(
        (element: {
          isCustomField: any;
          formTab: { name: any };
          customFieldsData: any[];
          name: any;
        }) => {
          if (!element.isCustomField && element.isCustomField == null) {
            var tabName = element.formTab;
            this._requiredFieldNames.push(tabName);
          }
          if (element.customFieldsData != undefined) {
            let thisvalue =
              element.customFieldsData.filter(
                (x: any) =>
                  x.entityId ==
                  (this._model.id > 0 ? this._model.id : this.copyModelId),
              ).length > 0
                ? element.customFieldsData.filter(
                  (x: any) =>
                    x.entityId ==
                    (this._model.id > 0 ? this._model.id : this.copyModelId),
                )[0].value
                : '';

            this._customValidation.push({
              name: element.name,
              value: thisvalue,
              tabName: element.formTab?.name,
            });
          }
        },
      );
    }, 500);
  }
  _deactivateTab(tabId: number, page: string) {
    let model: any = {
      customFieldEntityId: page == this._page ? 1 : 2,
      page: this._page,
      accountIds: this._selectedAccountIds,
    };
    this._customFieldsService
      .deleteCustomTab(tabId, model)

      .subscribe((response: any) => {
        if (response != null && response['message'] != null) {
          var deletedTab = this._dataTabs.find(
            (f) => f.id == tabId && f.isCustom == true,
          );
          this._dataTabs.splice(this._dataTabs.indexOf(deletedTab), 1);
          //go to zero based tab
          this.screenTabStrip.selectTab(0);
          this._customFieldsService
            .getFormTabsByPage(this._page)

            .subscribe((data: any) => {
              //get custom tabs first
              this._customTabs = data.filter((x: { isCustom: boolean; }) => x.isCustom == true);
            });
          this._layoutComponent.openPopup({
            title: 'Alert',
            message: response['message'],
            withoutConfirm: true,
          });

          if (response['close'] != null) {
            if (response['close'] == true) {
            } else {
              return;
            }
          }
        }
      });
  }

  _reorderingtabs() {
    this._orderingTabsForm = true;
  }

  _closeOrderingTabsForm() {
    this._orderingTabsForm = false;
    this._dataTabsSelected = this._userNewDataTabs;
  }

  _comboValueChange(e: any, i: number) {
    if (this._dataTabsSelected.find((x) => x.order == i + 1)) {
      this._userNewDataTabs.find(
        (x) => x === this._dataTabsSelected.find((x) => x.order == i + 1),
      ).order = this._dataTabsSelected.find((x) => x.name === e.name).order;
    }

    this._userNewDataTabs.find((obj) => obj.name == e.name).order = i + 1;
    //this._dataTabsSelected = this._dataTabsSelected.filter((obj) => obj !== e);
  }

  _onSaveTabOrdering() {
    this._gridFields
      .updateFormTabsReorder(this._userNewDataTabs)

      .subscribe((x: any) => {
        this._dataTabs = x;
        this._closeOrderingTabsForm();
      });
  }
  // #endregion

  // #region Window Events
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this._innerWidth = window.innerWidth;

    if (this._innerWidth > 450) {
      this._hiddenWindow = 'hidden';
      this._maximized = 'maximized';
    } else {
      this._hiddenWindow = '';
      this._maximized = 'default';
    }

    let windowSize = window.innerWidth;

    if (windowSize <= 991) {
      this._kendoTabStripPosition = 'top';
    } else {
      this._kendoTabStripPosition = 'left';
    }
  }

  // #endregion

  // #region Page Controls Actions
  _generateBarCode(): void {
    this._barcodeGeneratorsService
      .getBarcode('asset', 1)

      .subscribe((data: any) => {
        this._assetNumber.setValue(data.result.toString());
        this._form.patchValue({
          assetNumber: data.result.toString(),
        });

        if (this._pageState == PageState.ADD) {
          setTimeout(() => {
            if (this.kendoInputModelNumber != undefined) {
              this.kendoInputModelNumber.toggle();
              this.kendoInputModelNumber.focus();
            }
            /* this.kendoInputModelNumber.nativeElement.focus();
          let _modelNumberInpt = this.kendoInputModelNumber;
          _modelNumberInpt.nativeElement.classList.add('focusedInput'); */
          });
        }
      });
  }

  _NumberSubmitted(): void {
    if (
      this._form.controls.assetNumber.value != undefined &&
      this._form.controls.assetNumber.value.trim() != ''
    ) {
      setTimeout(() => {
        if (this.kendoInputModelNumber != undefined) {
          this.kendoInputModelNumber.toggle();
          this.kendoInputModelNumber.focus();
        }
      });
    }
  }

  getLabel(objectCode: string): GridFieldTextSummary {
    if (this._gridFieldText) {
      if (
        this._gridFieldText.find(
          ({ gridFieldDefinition }) =>
            gridFieldDefinition?.objectCode === objectCode,
        )
      )
        var result = this._gridFieldText.find(
          ({ gridFieldDefinition }) =>
            gridFieldDefinition?.objectCode === objectCode,
        );
      else result = null;
      return result ? result : null;
    }
  }
  // #endregion

  // #region Popup Action Buttons
  _onSave(e: string): any {

    var sitesSelected: boolean = true;
    if (this._currentUser.isEnterprise) {
      if (
        this._currentUser.isEnterprise &&
        this._selectedAccounts.length === 0
      ) {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please select account(s) to continue.',
        });
        return false;
      } else {
        var tmpAccountSites = this._selectedAccounts.filter(
          (x: any) => x.siteId != -1,
        );
        if (tmpAccountSites.length != this._selectedAccounts.length) {
          this._layoutComponent.openPopup({
            title: 'Validation',
            message:
              'Please select the site(s) of the selected account(s) to continue.',
          });
          sitesSelected = false;
          return false;
        }
      }
    }
    if (this._isMultiSiteSelected == true && !this._form.get('site').value) {
      this._layoutComponent.openPopup({
        title: 'Validation',
        message: 'Please select the site to continue.',
      });
      return false;
    }
    this._invalidForm = false;
    if (!this._invalidForm) {
      if (
        this._form.invalid &&
        this._customValidation.find((x: any) => !x.value)
      ) {
        this._invalidForm = true;
        this._invalidSubmit = true;
        this._additionalErrors.push('Additional Error');
        this._additionalErrors = [];
      } else if (this._form.invalid) {
        this._invalidForm = true;
      } else if (this._customValidation.find((x: any) => !x.value)) {
        this._additionalErrors.push('Additional Error');
        this._additionalErrors = [];
        this._invalidSubmit = true;
        this._invalidForm = true;
      } else {
        // work with custom fields
        if (this._customFieldsValue) {
          this._customFieldsValue.forEach((elementy: any) => {
            this._customFieldsDictionary[elementy.Key] = elementy.Value;
          });
        }
        this._invalidForm = false;
        this._invalidSubmit = false;
        if (
          (this._form.controls.modelNumber.value == null ||
            this._form.controls.modelNumber.value == undefined) &&
          this.isModelRequired == true
        ) {
          this._layoutComponent.openPopup({
            title: 'Alert',
            message: 'Please select a model',
            withoutConfirm: true,
          });
          return;
        }
        //Generate Request Model
        const model: IAssetDto = this.generateRequestModel();
        if (
          (this._pageState == PageState.ADD ||
            this._pageState == PageState.COPY) &&
          (this._model.id == null || this._model.id == 0)
        ) {
          this._assetService
            .createAsset(model)

            .subscribe((response: any) => {
              if (response != null && response['message'] != null) {
                if (!this._currentUser.isEnterprise) {
                  this.note.createNote(response.responseId);
                }
                this._layoutComponent.openPopup({
                  title: 'Alert',
                  message: response['message'],
                  withoutConfirm: true,
                });

                if (response['close'] != null) {
                  if (response['close'] == true) {
                    if (e === 'saveAndClose') {
                      // TODO :: This is error in Back and front when I make decetion with this arch I will fix it. :: PR => 5
                      model.id = +response['responseId'];
                      this._closeForm(model);
                    } else if (e === 'saveAndCopy') {
                      this._form.controls.assetNumber.setValue(null);
                      this.copyModelId = this._model.id;
                      this.loadCustomFields();
                      this._model.id = 0;
                      this._model['assetModelRelatedFields'].id = 0;
                      this._pageState = PageState.ADD;
                    } else if (e === 'save') {

                      // this._model.id = response['responseId'];
                      // this._model['assetModelRelatedFields'].id = response.extraData.assetModelRelatedFieldsId;
                      this._pageState = PageState.EDIT;
                      this._model = { ...response.extraData };
                      if (model.modelId == null || model.modelId == undefined) {
                        this.getAssetModelsDD();
                        this._form.controls.modelNumber.setValue(
                          this._model.model,
                        );
                      }
                    } else if (e === 'saveAndNew') {
                      this._active = false;
                      this._isDialiog = false;
                      this.openUpsert(this._page, {}, PageState.ADD);
                    }
                  } else {
                    return;
                  }
                }
              }
            });
        } else if (this._pageState == PageState.EDIT) {
          if (this._model.id == null) {
            this._model.id = model.id;
          }
          this._assetService
            .updateAsset(this._model.id, model)

            .subscribe((response: any) => {
              if (response != null && response['message'] != null) {
                this._layoutComponent.openPopup({
                  title: 'Alert',
                  message: response['message'],
                  withoutConfirm: true,
                });
                if (response['close'] != null) {
                  if (response['close'] == true) {
                    if (e === 'saveAndClose') {
                      this._closeForm(model);
                    } else if (e === 'saveAndCopy') {
                      this._form.controls.assetNumber.setValue(null);
                      this.copyModelId = this._model.id;
                      this.loadCustomFields();
                      this._model.id = 0;
                      this._model.assetModelRelatedFields.id = 0;
                      this._pageState = PageState.ADD;
                    } else if (e === 'save') {
                      this._pageState = PageState.EDIT;
                      this._model = { ...response.extraData };
                      if (model.modelId == null || model.modelId == undefined) {
                        this.getAssetModelsDD();
                        this._form.controls.modelNumber.setValue(
                          this._model.model,
                        );
                      }
                    } else if (e === 'saveAndNew') {
                      this._active = false;
                      this._isDialiog = false;
                      this.openUpsert(this._page, {}, PageState.ADD);
                    }
                  } else {
                    return;
                  }
                }
              }
            });
        }
      }
    }
  }
  private generateRequestModel(): IAssetDto {
    let _assetFiles: assetFile[] = [];
    _assetFiles.push(...this._assetFilesPics);
    _assetFiles.push(...this._assetFilesDoc);
    _assetFiles.push(...this._assetFilesVideo);
    const retModel: IAssetDto = <IAssetDto>{
      id: this._model?.id ?? 0,
      createdOn: this._model.createdOn ?? new Date(),
      assetName: this._form.controls.assetName.value,
      assetNumber: this._form.controls.assetNumber.value,
      model: this._form.controls.modelNumber.value,
      modelId: this._form.controls.modelNumber.value?.id,
      status: EntityStatusOptions.Active,
      acquisitionType: this._form.controls.acquisitionType.value,
      acquisitionTypeId: this._form.controls.acquisitionType.value?.id,
      availableForCheckout:
        this._form.controls.availableForCheckout.value ?? false,
      availableForCustodyTransfere:
        this._form.controls.availableForCustodyTransfer.value ?? false,
      serialNumber: this._form.controls.serialNumber.value,
      serialNumberIsUnique:
        this._form.controls.serialNumberIsUnique.value ?? false,
      serialNumberIsInsured:
        this._form.controls.serialNumberIsInsured.value ?? false,
      conditionId: this._form.controls.conditionCode.value?.id,
      condition: this._form.controls.conditionCode.value,
      assetStatusId: this._form.controls.assetStatusesCode.value?.id,
      assetStatus: this._form.controls.assetStatusesCode.value,
      personId: this._form.controls.personCode.value?.id,
      person: this._form.controls.personCode.value,
      homeLocationId: this._form.controls.homeLocationCode.value?.id,
      homeLocation: this._form.controls.homeLocationCode.value,
      locationId: this._form.controls.locationCode.value?.id,
      location: this._form.controls.locationCode.value,
      checkoutDueDate: null,
      description: this._form.controls.description.value,
      manufacturer: this._form.controls.manufacturer.value,
      purchaseOrderNumber: this._form.controls.purchaseOrderNumber.value,
      purchaseDate: this._form.controls.purchaseDate.value
        ? new Date(this._form.controls.purchaseDate.value)
        : null,
      purchaseOrderDate: this._form.controls.purchaseOrderDate.value
        ? new Date(this._form.controls.purchaseOrderDate.value)
        : null,
      purchasedFromId: this._form.controls.purchaseSupplierCode.value?.id,
      purchasedFrom: this._form.controls.purchaseSupplierCode.value,
      depreciationMethodId: this._form.controls.depreciationMethod.value?.id,
      depreciationMethod: this._form.controls.depreciationMethod.value,
      yearsToDepreciate: this._form.controls.yearsToDepreciate.value,
      cost: this._form.controls.purchaseCost.value,
      scrapValue: this._form.controls.scrapValue.value,
      depreciationTaken: this._form.controls.depreciationTaken.value ?? this._model?.depreciationTaken,
      depreciationTakenDate: this._form.controls.depreciationTakenDate.value
        ? new Date(this._form.controls.depreciationTakenDate.value)
        : this._model?.depreciationTakenDate,
      depreciationDate: this._form.controls.depreciationDate.value
        ? new Date(this._form.controls.depreciationDate.value)
        : this._model?.depreciationDate,
      bookValue: this._form.controls.bookValue?.value ?? this._model?.bookValue,
      fundingSource: this._form.controls.fundingSourceCode.value,
      fundingSourceId: this._form.controls.fundingSourceCode.value?.id,
      warrantyNumber: this._form.controls.warrantyNumber.value,
      warrantyStartDate: this._form.controls.warrantyStartDate.value
        ? new Date(this._form.controls.warrantyStartDate.value)
        : null,
      endOfLife: this._form.controls.endOfLife.value
        ? new Date(this._form.controls.endOfLife.value)
        : null,
      endOfSupport: this._form.controls.endOfSupport.value
        ? new Date(this._form.controls.endOfSupport.value)
        : null,
      warrantyEndDate: this._form.controls.warrantyEndDate.value
        ? new Date(this._form.controls.warrantyEndDate.value)
        : null,
      maintananceSupplierId:
        this._form.controls.maintananceSupplierCode.value?.id,
      maintananceSupplier: this._form.controls.maintananceSupplierCode.value,
      maintananceContractStartDate: this._form.controls
        .maintananceContractStartDate.value
        ? new Date(this._form.controls.maintananceContractStartDate.value)
        : null,
      maintananceContractEndDate: this._form.controls.maintananceContractEndDate
        .value
        ? new Date(this._form.controls.maintananceContractEndDate.value)
        : null,
      maintananceContractCost:
        this._form.controls.maintananceContractCost.value,
      assignedTo: this._model.assignedTo,
      assignedToId: this._model.assignedToId,
      leasedFrom: this._form.controls.leaseSupplierCode.value,
      leasedFromId: this._form.controls.leaseSupplierCode.value?.id,
      leaseNumber: this._form.controls.leaseNumber.value,
      checkedout: this._model?.checkedout ?? false,
      leaseEndDate: this._form.controls.leaseEndDate.value
        ? new Date(this._form.controls.leaseEndDate.value)
        : null,
      leaseStartDate: this._form.controls.leaseStartDate.value
        ? new Date(this._form.controls.leaseStartDate.value)
        : null,
      customFieldsValue: this._customFieldsDictionary,
      site: null,
      siteId: this._isMultiSiteSelected
        ? (this._form.get('site').value?.id ?? this.siteId)
        : this.siteId,
      assetFiles: <assetFile[]>_assetFiles,
      assetModelRelatedFieldsId: this._model?.assetModelRelatedFields?.id ?? 0,
      accountIds: this._selectedAccountIds.map((c) => c),
      assetSites: this._currentUser.isEnterprise
        ? this._selectedAccounts.map((c: any) => {
          return {
            siteId: this._form.get('site').value?.id ?? c.siteId,
            accountId: c.accountId,
          };
        })
        : null,
      assetModelRelatedFields: <IAssetModelRelatedFields>{
        id: this._model?.assetModelRelatedFields?.id ?? 0,
        status: EntityStatusOptions.Active,
        computerCPU: this._form.controls.rModelComputerCPU.value,
        computerCPUSpeed: this._form.controls.rModelComputerCPUSpeed.value,
        computerHardDriveSize:
          this._form.controls.rModelComputerHardDriveSize.value,
        computerIPAddress: this._form.controls.rModelComputerIPAddress.value,
        computerOS: this._form.controls.rModelComputerOS.value,
        computerBIOS: this._form.controls.rModelComputerBIOS.value,
        computerRAM: this._form.controls.rModelComputerRAM.value,
        computerVideoCard: this._form.controls.rModelComputerVideoCard.value,
        computerMACAddress: this._form.controls.rModelComputerMACAddress.value,
        furnitureSize: this._form.controls.rModelFurnitureSize.value,
        furnitureColor: this._form.controls.rModelFurnitureColor.value,
        furnitureHeight: this._form.controls.rModelFurnitureHeight.value,
        furnitureLength: this._form.controls.rModelFurnitureLength.value,
        furnitureWidth: this._form.controls.rModelFurnitureWidth.value,
        monitorResolution: this._form.controls.rModelMonitorResolution.value,
        monitorScreenSize: this._form.controls.rModelMonitorScreenSize.value,
        softwareVersion: this._form.controls.rModelSoftwareVersion.value,
        softwareLicenses: this._form.controls.rModelSoftwareLicenses.value,
        vehicleColor: this._form.controls.rModelVehicleColor.value,
        vehicleLicensePlate:
          this._form.controls.rModelVehicleLicensePlate.value,
        vehicleVIN: this._form.controls.rModelVehicleVIN.value,
        vehicleMileage: this._form.controls.rModelVehicleMileage.value,
        tabletsScreenSize: this._form.controls.rTabletsScreenSize.value,
        tabletsScreenResolution:
          this._form.controls.rTabletsScreenResolution.value,
        tabletsStorage: this._form.controls.rTabletsStorage.value,
        tabletsConnectivity: this._form.controls.rTabletsConnectivity.value,
        tabletsBatteryLife: this._form.controls.rTabletsBatteryLife.value,
        tabletsCamera: this._form.controls.rTabletsCamera.value,
        mobileDevicesRAM: this._form.controls.rMobileDevicesRAM.value,
        mobileDevicesStorage: this._form.controls.rMobileDevicesStorage.value,
        mobileDevicesBattery: this._form.controls.rMobileDevicesBattery.value,
        mobileDevicesOperatingSystem:
          this._form.controls.rMobileDevicesOperatingSystem.value,
        mobileDevicesScreenSize:
          this._form.controls.rMobileDevicesScreenSize.value,
        mobileDevicesCamera: this._form.controls.rMobileDevicesCamera.value,
        mobileDevicesDisplayQuality:
          this._form.controls.rMobileDevicesDisplayQuality.value,
        smartWatchesDimensions:
          this._form.controls.rSmartWatchesDimensions.value,
        smartWatchesWeight: this._form.controls.rSmartWatchesWeight.value,
        smartWatchesBuild: this._form.controls.rSmartWatchesBuild.value,
        smartWatchesSIM: this._form.controls.rSmartWatchesSIM.value,
        camerasNumberOfMegapixels:
          this._form.controls.rCamerasNumberOfMegapixels.value,
        camerasAperture: this._form.controls.rCamerasAperture.value,
        camerasFocalLength: this._form.controls.rCamerasFocalLength.value,
        camerasSensorSize: this._form.controls.rCamerasSensorSize.value,
        camerasZoomType: this._form.controls.rCamerasZoomType.value,
        camerasMethodsOfStabilization:
          this._form.controls.rCamerasMethodsOfStabilization.value,
        camerasFocusingSystem: this._form.controls.rCamerasFocusingSystem.value,
        tVsScreenSize: this._form.controls.rTVsScreenSize.value,
        tVsResolution: this._form.controls.rTVsResolution.value,
        tVsInputLag: this._form.controls.rTVsInputLag.value,
        tVsRefreshRate: this._form.controls.rTVsRefreshRate.value,
        tVsLED: this._form.controls.rTVsLED.value,
        tVsOLEDAndPlasma: this._form.controls.rTVsOLEDAndPlasma.value,
        tVsHDR: this._form.controls.rTVsHDR.value,
        tVsLocalDimming: this._form.controls.rTVsLocalDimming.value,
        tVsViewingAngle: this._form.controls.rTVsViewingAngle.value,
        tVsInternetConnectivity:
          this._form.controls.rTVsInternetConnectivity.value,
        tVsAppStore: this._form.controls.rTVsAppStore.value,
        tVsVoiceControl: this._form.controls.rTVsVoiceControl.value,
        tVsScreenMirroring: this._form.controls.rTVsScreenMirroring.value,
        tVsGaming: this._form.controls.rTVsGaming.value,
        tVsPorts: this._form.controls.rTVsPorts.value,
        tVsPictureQuality: this._form.controls.rTVsPictureQuality.value,
        tVsSoundQuality: this._form.controls.rTVsSoundQuality.value,
        networkEquipmentsEquipmentType:
          this._form.controls.rNetworkEquipmentsEquipmentType.value,
        networkEquipmentsAreaNetworkType:
          this._form.controls.rNetworkEquipmentsAreaNetworkType.value,
        networkEquipmentsFormFactor:
          this._form.controls.rNetworkEquipmentsFormFactor.value,
        networkEquipmentsNetworkMedia:
          this._form.controls.rNetworkEquipmentsNetworkMedia.value,
        networkEquipmentsNumberOfPorts:
          this._form.controls.rNetworkEquipmentsNumberOfPorts.value,
        networkEquipmentsDataRate:
          this._form.controls.rNetworkEquipmentsDataRate.value,
        networkEquipmentsPacketForwardingRate:
          this._form.controls.rNetworkEquipmentsPacketForwardingRate.value,
        networkEquipmentsBoard:
          this._form.controls.rNetworkEquipmentsBoard.value,
        networkEquipmentsChassisMount:
          this._form.controls.rNetworkEquipmentsChassisMount.value,
        networkEquipmentsbandwidthExpansion:
          this._form.controls.rNetworkEquipmentsbandwidthExpansion.value,
        networkEquipmentsVoIPBYOD:
          this._form.controls.rNetworkEquipmentsVoIPBYOD.value,
        networkEquipmentsNetworkSecurity:
          this._form.controls.rNetworkEquipmentsNetworkSecurity.value,
        cablesPartNumber: this._form.controls.rCablesPartNumber.value,
        cablesConductorSize: this._form.controls.rCablesConductorSize.value,
        cablesConductorDiameter:
          this._form.controls.rCablesConductorDiameter.value,
        cablesInsulationDiameter:
          this._form.controls.rCablesInsulationDiameter.value,
        cablesThickness: this._form.controls.rCablesThickness.value,
        cablesOverallDiameter: this._form.controls.rCablesOverallDiameter.value,
        cablesNetWeight: this._form.controls.rCablesNetWeight.value,
        routersModemsSwitchesRouterWithOrWithoutInternet:
          this._form.controls.rRoutersModemsSwitchesRouterWithOrWithoutInternet
            .value,
        routersModemsSwitchesWifiStandard:
          this._form.controls.rRoutersModemsSwitchesWifiStandard.value,
        routersModemsSwitchesWirelessFrequency:
          this._form.controls.rRoutersModemsSwitchesWirelessFrequency.value,
        routersModemsSwitchesSpeedOfTheRouter:
          this._form.controls.rRoutersModemsSwitchesSpeedOfTheRouter.value,
        routersModemsSwitchesRouterAntennaRange:
          this._form.controls.rRoutersModemsSwitchesRouterAntennaRange.value,
        recorderOutputLevel: this._form.controls.rRecorderOutputLevel.value,
        recorderFrequencyResponse:
          this._form.controls.rRecorderFrequencyResponse.value,
        recorderOutputImpedance:
          this._form.controls.rRecorderOutputImpedance.value,
        recorderDirectivity: this._form.controls.rRecorderDirectivity.value,
        externalHardDisksCapacities:
          this._form.controls.rExternalHardDisksCapacities.value,
        externalHardDisksInterface:
          this._form.controls.rExternalHardDisksInterface.value,
        externalHardDisksColours:
          this._form.controls.rExternalHardDisksColours.value,
        externalHardDisksSpeed:
          this._form.controls.rExternalHardDisksSpeed.value,
        headphonesDesign: this._form.controls.rHeadphonesDesign.value,
        headphonesComfort: this._form.controls.rHeadphonesComfort.value,
        headphonesSoundQuality:
          this._form.controls.rHeadphonesSoundQuality.value,
        faxResolution: this._form.controls.rFaxResolution.value,
        faxScannerType: this._form.controls.rFaxScannerType.value,
        faxType: this._form.controls.rFaxType.value,
        faxPrintTechnology: this._form.controls.rFaxPrintTechnology.value,
        faxScanningWidth: this._form.controls.rFaxScanningWidth.value,
        faxMemorySize: this._form.controls.rFaxMemorySize.value,
        staplingMachinesWeight:
          this._form.controls.rStaplingMachinesWeight.value,
        staplingMachinesCapability:
          this._form.controls.rStaplingMachinesCapability.value,
        staplingMachinesType: this._form.controls.rStaplingMachinesType.value,
        handToolsOrientation: this._form.controls.rHandToolsOrientation.value,
        handToolsSize: this._form.controls.rHandToolsSize.value,
        handToolsSpan: this._form.controls.rHandToolsSpan.value,
        handToolsMaterial: this._form.controls.rHandToolsMaterial.value,
        handToolsActivationMethods:
          this._form.controls.rHandToolsActivationMethods.value,
      },
      assetAssignees: this._assetAssignees,
    };

    return retModel;
  }
  _edit(): void {
    this._pageState = PageState.EDIT;
  }
  _openForm(componentName: string, objectname: any = null) {
    this._isDialiog = true;
    this._dialogComponentName = componentName;
    this._dialogModel = {
      model: {},
      pageState: PageState.ADD,
      reload: 'model',
      fileSource: fileSources.asset,
    };
    this._isMandatory = false;
    this.selectedobjectname = objectname;
  }
  _closeDialog(fileData: any) {
    this._isDialiog = false;
    this.loadScheduledMaintenanceData();
    this.loadPerformedMaintenanceHistoryData();
  }
  // #endregion

  // #region Custom Fields / Forms Actions
  formStatus(isInvalid: boolean) {
    this._custominvalidForm = isInvalid;
  }
  _onEditFields() {
    if (!this._currentUser.isEnterprise) {
      this._pageState = PageState.EDIT_FIELDS;
    } else {
      if (!this._selectedAccounts.length) {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please choose the desired account in the Accounts Tab',
          withoutConfirm: true,
        });
      } else {
        this._editFormActive = true;
        this._isCustomfieldsFormValue = true;
      }
    }
  }
  _addNewTab() {
    this._editFormActive = true;
    this._isAddTabFormValue = true;
    this._isRenameLabel = false;
    this._selectedLabelId = null;
  }
  _addNewField(tabId: number) {
    this._selectedTabId = null;
    this._selectedTabId = tabId;
    this._editFormActive = true;
    this._isCustomfieldsFormValue = true;
    this._isRenameLabel = false;
  }
  _openRenameForm(objectCode: string, isMandatory: boolean = false) {
    this._selectedLabelObjectCode = objectCode; //todo send system field label id
    //return; //open form once label id is set
    this._editFormActive = true;
    this._isRenameFormValue = true;
    this._isRenameCustomFormValue = false;
    this._isRenameLabel = true;
    this._isMandatory = isMandatory;
  }
  _openCustomRenameForm(customFieldId: number, tabId: number) {
    this._selectedLabelId = customFieldId;
    this._selectedTabId = tabId;
    this._editFormActive = !isNaN(customFieldId);
    this._isRenameFormValue = !isNaN(customFieldId);
    this._isRenameCustomFormValue = !isNaN(customFieldId);
    this._isRenameLabel = false;
    this._isMandatory = false;
  }
  _onEditForms() {
    this._isEditForms = true;
  }
  _onCloseEditForms(event: boolean) {
    this._isEditForms = event;
  }
  _returnAdNew() {
    this._pageState = PageState.ADD;
  }
  _viewInactiveFields() {
    this._getInactiveFields();
    /* this._customFieldsService
      .getDeactivatedCustomFields(this._entity, this._page)
      
      .subscribe((data: Array<any>) => {
        this._deactivatedFieldsGridData = data.filter(x => x.formTab.page === this._page);
      }); */
    this._deactivatedFieldsForm = true;
  }
  _reactivateField(item: any) {
    this.reactivateItem = item;
    this.reactivate = true;
    this._sharedService.okCloseClicked.subscribe((data: boolean) => {
      if (data && this.reactivate) {
        this.reactivate = false;
        this._customFieldsService
          .reactivateCustomField(this.reactivateItem.id, item)

          .subscribe((response: any) => {
            this.getSystemFieldsAndCustomFields();
            this._deactivatedFieldsForm = false;
            //get tabid,if not in list call get systemtabs
            var deletedTab = this._dataTabs.find(
              (f) => f.id == item.formTabId && f.isCustom == true,
            );
            if (deletedTab == undefined) {
              this.getFormTabs();
            }
            if (response != null && response['message'] != null) {
              this._layoutComponent.openPopup({
                title: 'Alert',
                message: response['message'],
                withoutConfirm: true,
              });
              /* if (response['close'] != null) {
                if (response['close'] == true) {
                  //this.loadCustomFields(this._model?.id?.toString());
                } else {
                  return;
                }
              } */
            }
            this._deactivatedFieldsForm = false;
          });
        this._deactivatedFieldsForm = false;
      } else {
        this.reactivate = false;
      }
    });

    this._layoutComponent.openPopup({
      title: 'Notification',
      message: 'Are you sure you want to Reactivate ' + item.name,
    });
    //this.loadCustomFields(this._model?.id?.toString());
    /* this.getSystemFieldsAndCustomFields();
    this._customFieldsService
    .getDeactivatedCustomFields(this._entity,this._page)
    
    .subscribe((data: Array<any>) => {
      this._deactivatedFieldsGridData = data.filter(x => x.formTab.page === this._page)
    }); */
  }
  _closeInactiveFields() {
    this._deactivatedFieldsForm = false;
  }
  dataMove(newLocData: any, oldLocData: any, dataTab?: FormTabSummary) {
    const sysF: GridFieldSummary[] = this._systemFields.filter(
      (f) => f.formTabId == dataTab.id,
    );
    const sysFClone: any[] = [];

    sysF.forEach((element: any) => {
      sysFClone.push(Object.assign({}, element));
    });
    sysF.find((x: { id: any }) => x.id === oldLocData.id).formOrder =
      sysFClone.find((x) => x.id === newLocData.id).formOrder;
    sysF.find((x: { id: any }) => x.id === newLocData.id).formOrder =
      sysFClone.find((x) => x.id === oldLocData.id).formOrder;

    this._editedSystemFields = sysF;
  }
  dragEnd() {
    this._gridFields
      .updatedSystemFieldReorder(this._editedSystemFields)

      .subscribe((x: GridFieldSummary[]) => {
        this._systemFields = x;
        this._editedSystemFields = x;
      });
  }
  _customFieldsValueChanges(customFieldsValue: any, tabId: number) {
    if (this._customFieldsValue == undefined) {
      this._customFieldsValue = [];
    }
    Object.keys(customFieldsValue).forEach((key) => {
      let customObject = {
        Key: key,
        Value: customFieldsValue[key],
      };
      let item = null;
      this._customFieldsValue.forEach((elementz: any) => {
        if (elementz.Key == key) {
          item = elementz;
        }
      });
      if (item != null) {
        let index = this._customFieldsValue.indexOf(item);
        if (index != -1) {
          this._customFieldsValue.splice(index, 1);
        }
      }
      var requiredCustomerField = this._customValidation.find(
        (x: any) => x.name == key,
      );
      if (requiredCustomerField) {
        let item2 = null;
        this._customValidation.forEach((element: any) => {
          if (element.name == key) {
            item2 = element;
          }
        });
        if (item2 != null) {
          let index2 = this._customValidation.indexOf(item2);
          if (index2 != -1) {
            //this._customValidation.splice(index2, 1);
            this._customValidation[index2].value = customObject.Value;
          }
          //this._customValidation.push({'name': customObject.Key, 'value': customObject.Value})
        }
      }
      this._additionalErrors = [];
      if (this._customValidation.find((x: any) => !x.value)) {
        this._additionalErrors.push('Additional Error');
        this._additionalErrors = [];
      }
      this._customFieldsValue.push(customObject);
    });
  }
  _removeCustomField(customFieldId: number) {
    for (var i = 1; i <= this._customFields.length; i++) {
      if (this._customFields[i] != null) {
        if (
          this._customFields[i].findIndex((x) => x.id == customFieldId) != -1
        ) {
          this._customFields[i] = this._customFields[i].filter(
            (x) => x.id != customFieldId,
          );
          return;
        }
      }
    }
  }
  ClosePopup(event: boolean) {
    if (event == true) {
      this._closeCustomFields();
    }
  }
  _closeCustomFields() {
    this._editFormActive = false;
    this._isCustomfieldsFormValue = false;
    this._isAddTabFormValue = false;
    this._isRenameFormValue = false;
    //this.loadCustomFields(this._model?.id?.toString());
    this.getSystemFieldsAndCustomFields();
    this.loadLabels();
    this.getFormTabs();
    this.getAuth();
  }
  _getInactiveFields() {
    this._customFieldsService
      .getDeactivatedCustomFields(this._entity, this._page)

      .subscribe((data: Array<any>) => {
        this._deactivatedFieldsGridData = data.filter(
          (x) => x.formTab.page === this._page,
        );
      });
  }
  // #endregion

  // #region Popup window Events
  openUpsert(componentName: string, model: any, pageState: PageState) {
    this._isDialiog = true;
    this._active = true;
    this._dialogComponentName = componentName;
    this._dialogModel = { model, pageState };
  }
  _onCancel() {
    if (this._model?.id && this._model.assetModelRelatedFields?.id) {
      this._closeForm(this._model);
    } else {
      this._closeForm();
    }
  }
  _closeForm(model?: any) {
    var closeComponent = {
      component: this._page,
      isClosed: true,
      response: model,
    };
    this._isDialiog = false;
    this._active = false;
    this._gridLayoutManagerService.setGridDataSubjects(this._page);
    //this.ngOnDestroy();
    this._layoutManager.removeOverlay();
    this._sharedService.closeDialog.emit(closeComponent);
    this._sharedService.model = {};
    //Quraim
    if (model) {
      closeComponent.response = model;
      this._dataService.push(closeComponent);
      this._sharedService.model = {};
    }
  }

  _onStateChanges(e: any) {
    var element: any = this.kendoMainWindow;

    if (e === 'minimized') {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else if (e === 'maximized') {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else if (e === 'default') {
      element.el.nativeElement.classList.add('fullWidthFormForm');
    }
  }

  // #endregion

  // #region Security Levels
  canAccess(scope: string, operation: string): boolean {
    return this._securityService.hasPermission(null, `${scope}-${operation}`);
  }
  getAuth() {
    // this._gridFields.getFileAuthorization(this._auth, this._currentUser.appRole.id)
    this._gridFields
      .getFieldsAuthorization(
        this._permissionScope,
        this._currentUser.appRole.id,
      )

      .subscribe((x: GridFieldAuthorization[]) => {
        this._gridFieldAuth = x;
      });
  }
  getAuthByLabel(objectCode: any) {
    if (this._gridFieldAuth) {
      if (
        this._gridFieldAuth.find(
          ({ gridFieldDefinition }) =>
            gridFieldDefinition?.objectCode === objectCode,
        )
      )
        var result = this._gridFieldAuth.find(
          ({ gridFieldDefinition }) =>
            gridFieldDefinition?.objectCode === objectCode,
        );
      else result = null;

      return result ? result : new GridFieldAuthorization();
    }
  }
  getAssetModelAuth() {
    this._gridFields
      .getFileAuthorization('mnuAssetModels', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._modelsAuth = x;
      });
  }
  getConditionsAuth() {
    this._gridFields
      .getFileAuthorization('mnuCondition', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._conditionsAuth = x;
      });
  }
  getStatusesAuth() {
    this._gridFields
      .getFileAuthorization('mnuAssetStatus', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._statusesAuth = x;
      });
  }
  getPersonsAuth() {
    this._gridFields
      .getFileAuthorization('mnuUser', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._personsAuth = x;
      });
  }
  getLocationsAuth() {
    this._gridFields
      .getFileAuthorization('mnuLocation', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._locationsAuth = x;
      });
  }
  getVendorsAuth() {
    this._gridFields
      .getFileAuthorization('mnuSupplier', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._vendorsAuth = x;
      });
  }
  getFundingsourcesAuth() {
    this._gridFields
      .getFileAuthorization('mnuFundingSource', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._fundingSourcesAuth = x;
      });
  }
  getSitesAuth() {
    this._gridFields
      .getFileAuthorization('mnuSites', this._currentUser.appRole.id)

      .subscribe((x: GridFieldAuthorization) => {
        this._siteAuth = x;
      });
  }
  // getCategoryAuth() {
  //     .getFileAuthorization('mnuModelCategory', this._currentUser.appRole.id)
  //     
  //     .subscribe((x: GridFieldAuthorization) => {
  //       this._categoryAuth = x;

  //     });
  // }
  // #endregion

  // #region File Library
  //Images / Picutres
  _getFilesGridFields() {
    if (this._filesGridFieldsSub) this._filesGridFieldsSub.unsubscribe();
    this._gridFields
      .getGridFieldsByPage(this._filesPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._filesPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._filesPageName,
      (gridColumns) => {
        this._filesGridFields = gridColumns;
      },
    );
  }
  _selectImagesHandler(e: any): void {
    if (this._currentUser.isEnterprise) {
      if (this._selectedAccountIds.length) {
        this._showImagesGrid = true;
      } else {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please choose the account with the site desired',
          withoutConfirm: true,
        });
      }
    } else if (!this._currentUser.isEnterprise) {
      if (
        this._filesGridFields == undefined ||
        this._filesGridFields.length == 0
      ) {
        this._getFilesGridFields();
      }
      this._showImagesGrid = true;
    }
  }
  _closeImagesWindow(): void {
    this._showImagesGrid = false;
    this._isDialiog = false;
  }
  _returnSelectedImagesHandler(e: any): void {
    if (this._currentUser.isEnterprise) {
      if (this._selectedAccountIds.length) {
        this._showImagesGrid = false;
        this._selectedImagesKeys = this._selectedImages;
        this._addPicture();
      } else {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please choose the account with the site desired',
          withoutConfirm: true,
        });
      }
    } else if (!this._currentUser.isEnterprise) {
      this._showImagesGrid = false;
      this._selectedImagesKeys = this._selectedImages;
      this._addPicture();
    }
  }
  _addPicture(fileData: any = null) {
    if (fileData == null) {
      this._assetFilesPics =
        this._assetFilesPics === null ? [] : this._assetFilesPics;
      if (this._selectedObjectImages.length > 0) {
        this._selectedObjectImages.forEach((element: any) => {
          let exists = this._assetFilesPics.find((f) => f.fileid == element.id);
          if (exists == undefined) {
            delete element.fileAccounts;
            let addedFileItem: assetFile = <assetFile>{
              fileid: element.id,
              filename: element.name,
              file: element,
              assetId: this._model.id ? this._model.id : 0,
              status: EntityStatusOptions.Active,
            };
            this._assetFilesPics.push(addedFileItem);
          }
        });
      }
    } else {
      let addedFileItem: assetFile = <assetFile>{
        fileid: fileData.response.id,
        filename: fileData.response.name,
        file: fileData.picObject.file,
        assetId: this._model.id ? this._model.id : 0,
        status: EntityStatusOptions.Active,
      };
      this._assetFilesPics.push(addedFileItem);
    }
  }
  _onImageRowSelectionChanges(e: any[]) {
    this._selectedImages = [];
    e.forEach((item) => {
      this._selectedImages.push(+item);
    });
  }
  _onImageObjectRowSelectionChanges(e: any[]) {
    this._selectedObjectImages = [];
    this._selectedObjectImages = e;
  }
  _getAccountImages = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    return this._filesService.getAccountsPictures(
      this._selectedAccountIds,
      params,
      model,
    );
  };
  _setPrimaryPic(data: any) {
    this._assetFilesPics.forEach((element: assetFile) => {
      if (element !== data) {
        element.assetFileisPrimary = false;
      } else {
        element.assetFileisPrimary = true;
      }
    });
  }
  _removePicHandler(event: any) {
    const index = this._assetFilesPics.indexOf(event, 0);
    if (index > -1) {
      this._assetFilesPics.splice(index, 1);
      this._selectedImagesKeys.splice(
        this._selectedImagesKeys.indexOf(event),
        1,
      );
    }
  }
  _removeFileFromLibrary(event: assetFile) {

    //Delete Logic Goes Here
    this._removePicHandler(event);
  }
  _onSelectedImagesChanged(e: any[]) { }
  _onSelectedImagesRowChanged(e: any[]) { }
  //Documents
  _getDocumentsGridFields() {
    if (this._filesGridFieldsSub) this._filesGridFieldsSub.unsubscribe();
    this._gridFields
      .getGridFieldsByPage(this._documentsPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._documentsPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._documentsPageName,
      (gridColumns) => {
        this._documentsGridFields = gridColumns;
      },
    );
  }
  _selectDocumentsHandler(e: any): void {
    if (this._currentUser.isEnterprise) {
      if (this._selectedAccountIds.length) {
        this._showDocumentGrid = true;
      } else {
        this._layoutComponent.openPopup({
          title: 'validation',
          message: 'Please choose the account with the site desired',
          withoutConfirm: true,
        });
      }
    } else if (!this._currentUser.isEnterprise) {
      if (
        this._documentsGridFields == undefined ||
        this._documentsGridFields.length == 0
      ) {
        this._getDocumentsGridFields();
      }
      this._showDocumentGrid = true;
    }
  }
  _closeDocumentsWindow(): void {
    this._showDocumentGrid = false;
  }
  _returnSelectedDocumentsHandler(e: any): void {
    if (this._currentUser.isEnterprise) {
      if (this._selectedAccountIds.length) {
        this._showDocumentGrid = false;
        this._selectedDocumentsKeys = this._selectedDocuments;
        this._addDocument();
      } else {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please choose the account with the site desired',
          withoutConfirm: true,
        });
      }
    } else if (!this._currentUser.isEnterprise) {
      this._showDocumentGrid = false;
      this._selectedDocumentsKeys = this._selectedDocuments;
      this._addDocument();
    }
  }
  _addDocument(fileData: any = null) {
    if (fileData == null) {
      this._assetFilesDoc =
        this._assetFilesDoc === null ? [] : this._assetFilesDoc;

      if (this._selectedObjectDocuments.length > 0) {
        this._selectedObjectDocuments.forEach((element: any) => {
          let exists = this._assetFilesDoc.find(
            (f) =>
              f.fileid == element.id && f.status == EntityStatusOptions.Active,
          );
          if (exists == undefined) {
            let addedFileItem: assetFile = <assetFile>{
              fileid: element.id,
              filename: element.name,
              file: element,
              assetId: this._model.id ? this._model.id : 0,
              status: EntityStatusOptions.Active,
            };
            this._assetFilesDoc.push(addedFileItem);
          }
        });
      }
    } else {
      let addedFileItem: assetFile = <assetFile>{
        fileid: fileData.response.id,
        filename: fileData.response.name,
        file: fileData.picObject.file,
        assetId: this._model.id ? this._model.id : 0,
        status: EntityStatusOptions.Active,
      };
      this._assetFilesDoc.push(addedFileItem);
    }
  }
  _removeDocHandler(event: any) {
    const index = this._assetFilesDoc.indexOf(event, 0);
    if (index > -1) {
      this._assetFilesDoc.splice(index, 1);
      this._selectedDocumentsKeys.splice(
        this._selectedDocumentsKeys.indexOf(event),
        1,
      );
    }
  }
  _onDocumentsRowSelectionChanges(e: any[]) {
    this._selectedDocuments = [];
    e.forEach((item) => {
      this._selectedDocuments.push(+item);
    });
  }
  _onDocumentObjectRowSelectionChange(e: any[]) {
    this._selectedObjectDocuments = [];
    this._selectedObjectDocuments = e;
  }
  _getAccountDocuments = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    return this._filesService.getDocuments(params, model);
  };
  _doDownloadFile(event: any) {
    let filePath = this._storagePath + event.file.stream;
    var a = document.createElement('a');
    a.href = filePath;
    document.body.appendChild(a);
    a.click();
    a.target = '_blank';
    document.body.removeChild(a);
  }
  //Videos
  _getVideosGridFields() {
    if (this._filesGridFieldsSub) this._filesGridFieldsSub.unsubscribe();
    this._gridFields
      .getGridFieldsByPage(this._videosPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._videosPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._videosPageName,
      (gridColumns) => {
        this._videosGridFields = gridColumns;
      },
    );
  }
  _selectVideosHandler(e: any): void {
    if (this._currentUser.isEnterprise) {
      if (this._selectedAccountIds.length) {
        this._showVideoGrid = true;
      } else {
        this._layoutComponent.openPopup({
          title: 'validation',
          message: 'Please choose the account with the site desired',
          withoutConfirm: true,
        });
      }
    } else if (!this._currentUser.isEnterprise) {
      if (
        this._videosGridFields == undefined ||
        this._videosGridFields.length == 0
      ) {
        this._getVideosGridFields();
      }
      this._showVideoGrid = true;
    }
  }
  _closeVideosWindow(): void {
    this._showVideoGrid = false;
  }
  _returnSelectedVideosHandler(e: any): void {
    if (this._currentUser.isEnterprise) {
      if (this._selectedAccountIds.length) {
        this._showVideoGrid = false;
        this._selectedVideosKeys = this._selectedVideos;
        this._addVideo();
      } else {
        this._layoutComponent.openPopup({
          title: 'Validation',
          message: 'Please choose the account with the site desired',
          withoutConfirm: true,
        });
      }
    } else if (!this._currentUser.isEnterprise) {
      this._showVideoGrid = false;
      this._selectedVideosKeys = this._selectedVideos;
      this._addVideo();
    }
  }
  _addVideo(fileData: any = null) {
    if (fileData == null) {
      this._assetFilesVideo =
        this._assetFilesVideo === null ? [] : this._assetFilesVideo;

      if (this._selectedObjectVideos.length > 0) {
        this._selectedObjectVideos.forEach((element: any) => {
          let exists = this._assetFilesVideo.find(
            (f) =>
              f.fileid == element.id && f.status == EntityStatusOptions.Active,
          );
          if (exists == undefined) {
            let addedFileItem: assetFile = <assetFile>{
              fileid: element.id,
              filename: element.name,
              file: element,
              assetId: this._model.id ? this._model.id : 0,
              status: EntityStatusOptions.Active,
            };
            this._assetFilesVideo.push(addedFileItem);
          }
        });
      }
    } else {
      let addedFileItem: assetFile = <assetFile>{
        fileid: fileData.response.id,
        filename: fileData.response.name,
        file: fileData.picObject.file,
        assetId: this._model.id ? this._model.id : 0,
        status: EntityStatusOptions.Active,
      };
      this._assetFilesVideo.push(addedFileItem);
    }
  }
  _removeVideoHandler(event: any) {
    const index = this._assetFilesVideo.indexOf(event, 0);
    if (index > -1) {
      this._assetFilesVideo.splice(index, 1);
      this._selectedVideosKeys.splice(
        this._selectedVideosKeys.indexOf(event),
        1,
      );
    }
  }
  _onVideosRowSelectionChanges(e: any[]) {
    this._selectedVideos = [];
    e.forEach((item) => {
      this._selectedVideos.push(+item);
    });
  }
  _onVideoObjectRowSelectionChange(e: any[]) {
    this._selectedObjectVideos = [];
    this._selectedObjectVideos = e;
  }
  _getAccountVideos = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    return this._filesService.getVideos(params, model);
  };
  _doDownloadVideo(event: any) {
    let filePath = this._storagePath + event.file.stream;
    var a = document.createElement('a');
    a.href = filePath;
    document.body.appendChild(a);
    a.click();
    a.target = '_blank';
    document.body.removeChild(a);
  }
  // #endregion

  // #region All History
  _getAllHistoryGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._historyPageName)

      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._historyPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._historyPageName,
      (gridColumns) => {
        this._historyGridFields = gridColumns;
      },
    );
  }
  _getGridHistoryData = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    return this._assetAllHistoryService.getAssetHistoryGrid(
      params,
      model,
      this._model?.assetNumber,
    );
  };
  // #endregion

  // #region Scheduled Maintenance Tab
  _getScheduledMaintenanceGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._scheduledMaintenancePageName)
      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._scheduledMaintenancePageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._scheduledMaintenancePageName,
      (gridColumns) => {
        this._scheduledMaintenanceGridFields = gridColumns;
      },
    );
  }

  loadScheduledMaintenanceData(): void {
    const assetId = this._model?.id ?? this._form?.get('id')?.value;
    const assetNumber = this._model?.assetNumber ?? this._form?.get('assetNumber')?.value;
    if (!assetId && !assetNumber) {
      this._scheduledMaintenanceList = [];
      return;
    }
    this._scheduledMaintenanceLoading = true;
    const params: any = { skip: 0, take: 50 };
    if (assetId) {
      params.assetId = assetId;
    }
    if (assetNumber) {
      params.assetNumber = assetNumber;
    }

    this._scheduleMaintenanceService.getGridData(params, null).subscribe({
      next: (res: any) => {
        this._scheduledMaintenanceList = res?.data || res?.items || (Array.isArray(res) ? res : []);
        this._scheduledMaintenanceLoading = false;
      },
      error: () => {
        this._scheduledMaintenanceLoading = false;
      },
    });
  }

  _getGridScheduledMaintenanceData = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    params = params || {};
    const assetId = this._model?.id ?? this._form?.get('id')?.value;
    const assetNumber = this._model?.assetNumber ?? this._form?.get('assetNumber')?.value;
    if (assetId) {
      params.assetId = assetId;
    }
    if (assetNumber) {
      params.assetNumber = assetNumber;
    }
    return this._scheduleMaintenanceService.getGridData(params, model);
  };

  openAddScheduleMaintenance(): void {
    const assetId = this._model?.id ?? this._form?.get('id')?.value;
    const assetNumber = this._model?.assetNumber ?? this._form?.get('assetNumber')?.value;
    const assetName = this._model?.assetName ?? this._form?.get('assetName')?.value;
    this.openUpsert(
      PageNames.ScheduleMaintenance,
      {
        assetId: assetId,
        assetNumber: assetNumber,
        assetName: assetName,
        transactionAssetNumber: assetNumber,
      },
      PageState.ADD,
    );
  }

  getScheduleStatusBadgeClass(item: any): string {
    if (item?.isPerformed) {
      return 'statusStyle request-status approved';
    } else if (item?.isOverdue) {
      return 'statusStyle request-status rejected';
    } else {
      return 'statusStyle request-status pending';
    }
  }

  getScheduleStatusLabel(item: any): string {
    if (item?.isPerformed) return 'Performed';
    if (item?.isOverdue) return 'Overdue';
    return 'Scheduled';
  }
  // #endregion

  // #region Maintenance History Tab
  loadPerformedMaintenanceHistoryData(): void {
    const assetId = this._model?.id ?? this._form?.get('id')?.value;
    const assetNumber = this._model?.assetNumber ?? this._form?.get('assetNumber')?.value;
    if (!assetId && !assetNumber) {
      this._performedMaintenanceHistoryList = [];
      return;
    }
    this._performedMaintenanceHistoryLoading = true;
    const params: any = { skip: 0, take: 50 };
    if (assetId) {
      params.assetId = assetId;
    }
    if (assetNumber) {
      params.assetNumber = assetNumber;
    }

    this._performMaintenanceService.getPerformedMaintenanceHistoryGrid(params, null).subscribe({
      next: (res: any) => {
        this._performedMaintenanceHistoryList = res?.data || res?.items || (Array.isArray(res) ? res : []);
        this._performedMaintenanceHistoryLoading = false;
      },
      error: () => {
        this._performedMaintenanceHistoryLoading = false;
      },
    });
  }

  _getPerformedMaintenanceHistoryGridFields() {
    this._gridFields
      .getGridFieldsByPage(this._performedMaintenanceHistoryPageName)
      .subscribe((data: any) => {
        this._gridLayoutManagerService.setGridColumnSubjects(
          this._performedMaintenanceHistoryPageName,
          data,
        );
      });
    this._gridLayoutManagerService.subscribeToColumnsChanges(
      this._performedMaintenanceHistoryPageName,
      (gridColumns) => {
        this._performedMaintenanceHistoryGridFields = gridColumns;
      },
    );
  }

  _getGridPerformedMaintenanceHistoryData = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    params = params || {};
    const assetId = this._model?.id ?? this._form?.get('id')?.value;
    const assetNumber = this._model?.assetNumber ?? this._form?.get('assetNumber')?.value;
    if (assetId) {
      params.assetId = assetId;
    }
    if (assetNumber) {
      params.assetNumber = assetNumber;
    }
    return this._performMaintenanceService.getPerformedMaintenanceHistoryGrid(params, model);
  };

  showMaintenanceDocuments(files: any[]): void {
    this.maintenanceDocumentsData = [];
    if (files && files.length > 0) {
      this.showMaintenanceDocumentsWindow = true;
      files.forEach((element: any) => {
        const fileId = element.fileId || element.id || element.file?.id;
        if (fileId) {
          this.maintenanceDocumentsData.push(fileId);
        }
      });
    }
  }

  closeMaintenanceDocumentsWindow(event: any): void {
    this.showMaintenanceDocumentsWindow = event;
  }
  // #endregion
  isFieldEnabled(fieldName: string): boolean {
    let _retVal: boolean = false;
    _retVal =
      this.getAuthByLabel(fieldName)?.canEdit &&
      (this._pageState == 2 || this._pageState == 3 || this._pageState == 5);
    return _retVal;
  }
  isTabName(tabName: string, tabNameCompare: string): boolean {
    //This is crazy
    let _retVal: boolean = false;
    _retVal = tabName == tabNameCompare;
    return _retVal;
  }
  isWindowEnabled(_popupName: string): boolean {
    let _retVal: boolean = false;
    switch (_popupName) {
      case 'notes': {
        _retVal = this._showFileNotes;
        break;
      }
    }
    return _retVal;
  }
  _onAccountRowSelectionChanges(e: any[]) {
    super._onAccountRowSelectionChanges(e, () => {
      setTimeout(() => {
        this.getAssetModelsDD();
        this.getLocationsDD();
        this.getConditionsDD();
        this.getStatusesDD();
        this.getPersonsDD();
        this.getVendorsDD();
        this.getFundingsourcesDD();
      }, 200);
    });
  }
  _getAccounts = (
    params?: any,
    model?: any,
  ): Observable<DataWithPagingMetadata<any>> => {
    return this._accountsService.getAccounts(params, model);
  };

  private parseLocalDate(dateVal: any): Date | null {
    if (!dateVal) return null;
    if (dateVal instanceof Date) return dateVal;
    if (typeof dateVal === 'string') {
      const match = dateVal.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      }
    }
    return new Date(dateVal);
  }
}
